import { toast } from 'react-hot-toast';
import { SignalAssetType, SignalSyncs, EMPTY_SIGNAL_SYNCS, SIGNAL_ASSET_TYPES } from '../utils/signalSync';
import { getToken, getSessionId } from './tokenManager';
import { data } from 'react-router-dom';

export const API_URL = process.env.REACT_APP_API_URL;
// const API_URL = "https://wethaiinvest.api-playground.memolab.me";
// export const API_URL = "https://stg-api.wethaiinvest.com";

// Backend error.code values that mean the session/token is genuinely invalid
// (as opposed to a network error, timeout, or other transient failure where
// no response was even received). Only these should ever trigger a logout.
// Backend sends these as either a number or a numeric string depending on
// the endpoint, so compare against the normalized Number() form everywhere.
export const AUTH_INVALID_CODES = [401000, 401200, 401301, 400000];

export function isAuthInvalidCode(code: unknown): boolean {
  const normalized = Number(code);
  return !Number.isNaN(normalized) && AUTH_INVALID_CODES.includes(normalized);
}

// ข้อความเฉพาะของแต่ละ error code เอาไว้ใช้เป็นหลัก เพราะบาง endpoint (เช่น 400000
// "Unauthorized" ธรรมดา) ไม่ได้ส่ง th_message ที่อธิบายสาเหตุจริงๆ มาให้ผู้ใช้เห็น
const AUTH_INVALID_MESSAGES: Record<number, string> = {
  401000: 'เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่อีกครั้ง',
  401200: 'ไม่พบข้อมูลผู้ใช้งานนี้ในระบบ กรุณาเข้าสู่ระบบใหม่อีกครั้ง',
  401301: 'บัญชีของคุณถูกเข้าสู่ระบบจากอุปกรณ์อื่น กรุณาเข้าสู่ระบบใหม่อีกครั้ง',
  400000: 'โทเค็นไม่ถูกต้องหรือหมดอายุ กรุณาเข้าสู่ระบบใหม่อีกครั้ง',
};

// เวลา (ms) ที่จะรอ ก่อนจะเคลียร์ token จริงๆ และ redirect ไปหน้า login
// ให้เวลาผู้ใช้ได้อ่าน toast ก่อนที่ session จะถูกตัด
const AUTH_INVALID_LOGOUT_DELAY_MS = 5000;

// เคลียร์ session ทิ้งเมื่อ backend ยืนยันว่า token ใช้ไม่ได้จริงๆ (ตาม isAuthInvalidCode)
// ทำเป็นจุดกลางจุดเดียว กันไม่ให้ toast/redirect ซ้ำซ้อนเวลามีหลาย request พังพร้อมกัน
let isHandlingAuthInvalid = false;
export function handleAuthInvalidToken(code: unknown, message?: string) {
  if (!isAuthInvalidCode(code)) return false;
  if (isHandlingAuthInvalid) return true;
  isHandlingAuthInvalid = true;

  const normalizedCode = Number(code);
  // ข้อความเฉพาะของ code นั้นๆ มาก่อนเสมอ ถ้าไม่มีที่กำหนดไว้ค่อย fallback ไปใช้ข้อความจาก backend
  const displayMessage = AUTH_INVALID_MESSAGES[normalizedCode] || message || 'Session expired. Please login again.';
  toast.error(displayMessage, { duration: AUTH_INVALID_LOGOUT_DELAY_MS });

  setTimeout(() => {
    localStorage.removeItem('userInfo');
    localStorage.removeItem('token');
    // แนบหน้าปัจจุบันไปกับ ?redirect= เพราะ window.location.href รีโหลดทั้งหน้า ทำให้ location.state หาย
    // (เช่น เปิดลิงก์บทความจาก Telegram แล้ว token หมดอายุ -> Login เสร็จต้องกลับมาหน้าบทความเดิม)
    const { pathname, search } = window.location;
    const isLoginPath = pathname.startsWith('/login');
    window.location.href = isLoginPath ? '/login' : `/login?redirect=${encodeURIComponent(pathname + search)}`;
  }, AUTH_INVALID_LOGOUT_DELAY_MS);
  return true;
}

// Helper function to handle API errors
const handleApiError = async (response: Response, defaultMessage: string) => {
  let errorMessage = defaultMessage;
  let thMessage = '';
  let errorCode: unknown = '';
  let errorDetails: any = null;
  let traceId = '';

  try {
    const errorData = await response.json();
    // โครงสร้างจริงจาก backend คือ { error: { code, message, th_message, details } }
    // แต่บาง endpoint (เช่น benchmark) ส่งแบบ flat มาที่ top-level พร้อม error: {} (object ว่าง) มาด้วย
    // ถ้าเช็คแค่ errorData.error || errorData จะได้ {} ว่างๆ เพราะ {} เป็น truthy ต้องเช็คว่ามีเนื้อหาจริงก่อน
    // และบาง endpoint (เช่น bulk) ส่ง code/message/th_message ที่ top-level แต่ error มีแค่ { details } เลยต้อง fallback ไป top-level ด้วย
    const errorBody =
      errorData.error && Object.keys(errorData.error).length > 0 ? errorData.error : errorData;

    if (errorBody.th_message || errorData.th_message) {
      thMessage = errorBody.th_message || errorData.th_message;
    }
    if (errorBody.message || errorData.message) {
      errorMessage = errorBody.message || errorData.message;
    }
    const rawCode = errorBody.code ?? errorData.code;
    if (rawCode !== undefined && rawCode !== null && rawCode !== '') {
      errorCode = rawCode;
    }
    errorDetails = errorBody.details ?? null;
    traceId = errorData.meta?.traceId || '';
  } catch (parseError) {
    console.error('Failed to parse error response:', parseError);
  }

  // ถ้า code คือกลุ่มที่ token ใช้ไม่ได้จริงๆ ให้ล้าง session ทันที ไม่ว่าจะเป็น endpoint ไหนก็ตาม
  handleAuthInvalidToken(errorCode, thMessage || errorMessage);

  const error = new Error(errorMessage);

  // แนบ Property พื้นฐานของ Response
  (error as any).status = response.status;
  (error as any).statusText = response.statusText;

  // ✅ แนบ Property พิเศษจาก API หลังบ้าน
  (error as any).th_message = thMessage;
  (error as any).code = errorCode;
  (error as any).details = errorDetails;
  (error as any).traceId = traceId;

  throw error;
};

export async function Logins(googleAccessToken: string): Promise<any> {
  const sessionId = getSessionId();
  const url = `${API_URL}/auth/google/token`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'X-Session-ID': `${sessionId}`, // ส่ง sessionId ใน headers
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ access_token: googleAccessToken }),
  });


  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));

    // ✅ ดึง th_message จากโครงสร้าง: error -> details -> th_message
    const thaiMessage = errorData.error?.details?.th_message;
    const defaultMessage = 'การเข้าสู่ระบบล้มเหลว';
    const englishMessage = errorData.error?.details?.en_message;
    const error: any = new Error(thaiMessage || englishMessage || defaultMessage);
    error.code = errorData.error?.code;
    error.status = res.status;

    throw error;
  }
  // if (!res.ok) {
  //   await handleApiError(res, 'Failed to fetch Google token');
  // }
  return res.json();
}

// Get My Portfolio
export async function getMyPortfolio(): Promise<any> {
  const authToken = getToken();
  // //console.log('getMyPortfolio - Token:', authToken);
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/portfolios`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to fetch portfolio');
  }
  return res.json();
}

// Get Portfolio by Symbol
export async function getPortfolioBySymbol(symbol: string): Promise<any> {
  const authToken = getToken();
  // //console.log('getPortfolioBySymbol - Token:', authToken);
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/portfolios/symbol/${symbol}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to get portfolio by symbol');
  }
  return res.json();
}

// Create Portfolio
export async function createPortfolio(portfolioData: {
  name: string;
  description: string;
  expectedRatio: number;
  startDate?: string;
}): Promise<any> {
  const authToken = getToken();
  //console.log('createPortfolio - Token:', authToken);
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/portfolios`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(portfolioData),
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to create portfolio');
  }
  return res.json();
}

// Update Portfolio
export async function updatePortfolio(portId: string, portfolioData: {
  name: string;
  description: string;
  expectedRatio: number;
}): Promise<any> {
  const authToken = getToken();
  //console.log('updatePortfolio - Token:', authToken);
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/portfolios/${portId}`;
  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(portfolioData),
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to update portfolio');
  }
  return res.json();
}

// Delete Portfolio
export async function deletePortfolio(portId: string): Promise<any> {
  const authToken = getToken();
  //console.log('deletePortfolio - Token:', authToken);
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/portfolios/${portId}`;
  const res = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to delete portfolio');
  }
  return res.json();
}

// Add Stock to Portfolio
export async function addStockToPortfolio(portId: string, stockData: {
  stockSymbol: string;
  quantity: number;
  avgBuyPrice: number;
  expectedRatio: number;
}): Promise<any> {
  const authToken = getToken();
  //console.log('addStockToPortfolio - Token:', authToken);
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/portfolios/${portId}/stocks`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(stockData),
  });
  if (!res.ok) {
    // console.log('addStockToPortfolio Error:', res.statusText);
    await handleApiError(res, 'Failed to add stock to portfolio');
  }
  return res.json();
}

// Update Stock in Portfolio
export async function updateStockInPortfolio(portId: string, symbol: string, stockData: {
  quantity: number;
  avgBuyPrice: number;
  expectedRatio: number;
}): Promise<any> {
  const authToken = getToken();
  //console.log('updateStockInPortfolio - Token:', authToken);
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/portfolios/${portId}/stocks/${symbol}`;
  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(stockData),
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to update stock in portfolio');
  }
  return res.json();
}

// Delete Stock from Portfolio
export async function deleteStockFromPortfolio(portId: string, symbol: string): Promise<any> {
  const authToken = getToken();
  //console.log('deleteStockFromPortfolio - Token:', authToken);
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/portfolios/${portId}/stocks/${symbol}`;
  const res = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to delete stock from portfolio');
  }
  return res.json();
}

// Get User Info
export async function getUserInfo(): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/users/me`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  // if (res.status === 401) {
  //   window.location.href = '/login'; // หรือใช้ navigate ถ้าอยู่ใน component
  //   return null;
  // }
  if (!res.ok) {
    const errorResponse = await res.json().catch(() => ({}));
    const errorCode = errorResponse.error?.code;
    const errorMessage = errorResponse.error?.th_message || errorResponse.error?.message || "Session expired. Please login again.";

    // เตือนและเด้งไปหน้า login เฉพาะตอนที่ error.code บ่งบอกว่า session/token ใช้ไม่ได้จริงๆ
    // (ไม่ใช่กรณีเน็ตหลุด/ไม่มี response ที่ควรจะข้ามไปเงียบๆ)
    handleAuthInvalidToken(errorCode, errorMessage);

    const error: any = new Error(errorMessage);
    error.code = errorCode;
    error.status = res.status;
    throw error; // Throw error to handle it in fetchUser
  }
  return res.json();
}

// Get User Lists
export async function getUserLists(options?: {
  page?: string;
  limit?: string;
  isAdmin?: string;
  isActive?: string;
  q?: string;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');
  const params = new URLSearchParams();
  if (options?.page) params.append('page', options.page);
  if (options?.limit) params.append('limit', options.limit);
  if (options?.isAdmin) params.append('isAdmin', options.isAdmin);
  if (options?.isActive) params.append('isActive', options.isActive);
  if (options?.q) params.append('q', options.q);

  const url = `${API_URL}/users/list?${params.toString()}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to fetch user list');
  }
  return res.json();
}

// Get Guru Info
export async function getGuru(): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/gurus`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to fetch guru info');
  }
  return res.json();
}

// 1. Create Article
export async function createArticle(articleData: {
  guruId: string;
  portfolioId: string;
  tickers: string[];
  tags: string[];
  title: string;
  content: string;
  status: string;
  cover: string
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');
  const url = `${API_URL}/articles`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(articleData),
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to create article');
  }
  return res.json();
}

// Update Article
export async function updatedArticle(
  articleId: string,
  articleData: {
    guruId: string;
    portfolioId: string;
    tickers: string[];
    tags: string[];
    title: string;
    content: string;
    status: string;
    cover: string;
  }
): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');
  const url = `${API_URL}/articles/${articleId}`;
  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(articleData),
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to update article');
  }
  return res.json();
}

// Delete Article
export async function deleteArticle(articleId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');
  const url = `${API_URL}/articles/${articleId}`;
  const res = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to delete article');
  }
  return res.json();
}

// Queue notification สำหรับ article ที่ publish อยู่แล้ว (ไม่เปลี่ยน article status)
// การ create/update article ให้ status=published ไม่ส่ง notification อัตโนมัติ ต้องเรียก endpoint นี้เอง
export async function queueArticleNotification(
  articleId: string,
  channels: Array<'IN_APP' | 'WEB_PUSH' | 'TELEGRAM'>
): Promise<{ articleId: string; eventId: string; status: string; channels: string[] }> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');
  const url = `${API_URL}/articles/${articleId}/published`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify({ channels }),
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to queue article notification');
  }
  return res.json();
}

// Publish notification สำหรับ Realtime V2 snapshot ผ่าน Notification Platform ใหม่
// IN_APP/WEB_PUSH ไปตาม canonical PORTFOLIO/REALTIME_PUBLISHED; TELEGRAM ยังวิ่งผ่าน legacy
// broadcast เดิม (portfolio template AS/AD/AP) อยู่ดี ไม่ใช่ generic Telegram renderer ของ platform ใหม่
// (ดู WeThaiInvest_Notification_Category_Type_Guide 06 · REALTIME V2 — TELEGRAM EXCEPTION)
// ขอ channel เดิมซ้ำจะได้ ALREADY_REQUESTED แต่เพิ่ม channel ที่ยังไม่เคยขอได้เรื่อย ๆ
export async function publishRealtimeV2Notification(
  realtimeId: string,
  channels: Array<'IN_APP' | 'WEB_PUSH' | 'TELEGRAM'>
): Promise<{ realtimeId?: string; status?: string; channels?: string[]; [key: string]: any }> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');
  const url = `${API_URL}/realtime-v2/${realtimeId}/publish`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify({ channels }),
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to publish realtime notification');
  }
  const text = await res.text();
  return text ? JSON.parse(text) : { status: 'QUEUED', channels };
}

// 2. Find by Ticker
export async function findArticleByTicker(ticker: string, limit?: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');
  const params = new URLSearchParams();
  if (ticker) params.append('ticker', ticker);
  if (limit) params.append('limit', limit);
  const url = `${API_URL}/articles?${params.toString()}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to find article by ticker');
  }
  return res.json();
}

// 3. Find by Guru
export async function findArticleByGuru(guruId: string, limit?: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');
  const params = new URLSearchParams();
  if (limit) params.append('limit', limit);
  if (guruId) params.append('guruId', guruId);
  const url = `${API_URL}/articles?${params.toString()}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to find article by guru');
  }
  return res.json();
}

// 4. Find by Guru Port
export async function findArticleByGuruPort(guruId: string, portfield: string, limit?: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');
  const params = new URLSearchParams();
  if (limit) params.append('limit', limit);
  if (guruId) params.append('guruId', guruId);
  if (portfield) params.append('portfield', portfield);
  const url = `${API_URL}/articles?${params.toString()}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to find article by guru port');
  }
  return res.json();
}

// 5. Find by Title
export async function findArticleByTitle(title: string, limit?: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');
  const params = new URLSearchParams();
  if (limit) params.append('limit', limit);
  if (title) params.append('title', title);
  const url = `${API_URL}/articles?${params.toString()}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to find article by title');
  }
  return res.json();
}

export async function getArticleById(articleId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');
  const url = `${API_URL}/articles/${articleId}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to fetch article by id');
  }
  return res.json();
}

// 6. Find Smarter [All]
export async function findSmarterArticlesAll(options: {
  expand?: string;
  includeAttributes?: string;
  guruId?: string;
  portfield?: string;
  ticker?: string;
  title?: string;
  page?: string;
  pages?: string;
  limit?: string;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');
  const params = new URLSearchParams();
  if (options.expand) params.append('expand', options.expand);
  if (options.includeAttributes) params.append('includeAttributes', options.includeAttributes);
  if (options.guruId) params.append('guruId', options.guruId);
  if (options.portfield) params.append('portfield', options.portfield);
  if (options.ticker) params.append('ticker', options.ticker);
  if (options.title) params.append('title', options.title);
  if (options.page) params.append('page', options.page);
  if (options.pages) params.append('pages', options.pages);
  if (options.limit) params.append('limit', options.limit);
  const url = `${API_URL}/articles?${params.toString()}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to find smarter articles');
  }
  return res.json();
}

export async function createPortfolioNew(portfolioData: {
  portName: string;
  ownerId: string;
  ownerName: string;
  description: string;
  stocks: any[];
  sells: any[];
  yesterday: any;
  updatedBy: string;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/realtime-portfolio/new`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(portfolioData),
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to create portfolio (new)');
  }
  return res.json();
}

export async function getAllPortfoliosNew(options?: {
  limit?: string;
  page?: string;
  owner?: string;
  isNotify?: boolean;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const params = new URLSearchParams();
  if (options?.limit) params.append('limit', options.limit);
  if (options?.page) params.append('page', options.page);
  if (options?.owner) params.append('owner', options.owner);
  if (options?.isNotify) params.append('isNotify', String(options.isNotify));

  const url = `${API_URL}/realtime-portfolio${params.toString() ? `?${params.toString()}` : ''}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to fetch all portfolios (new)');
  }
  return res.json();
}

export async function getRealtimePortfolioPortIds(portids: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/realtime-portfolio/${portids}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to get realtime portfolio portids');
  }
  return res.json();
}

export async function updatePortfolioNew(
  portId: string,
  portfolioData: {
    portName: string; // <-- Add this line
    description: string;
    stocks: Array<{
      ticker: string;
      currentValue: number;
      currentRatio: number;
      currentAvgPrice: number;
      note?: string;
      isNew?: boolean;
      isHilight?: boolean;
    }>;
    sells: Array<{
      ticker: string;
      sellValue: number;
      sellRatio: number;
      remainingValue: number;
      remainingRatio: number;
      isAll?: boolean;
      note?: string;
      isHilight?: boolean;
    }>;
    updatedBy: string;

  }
): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');
  // Hardcode isNotify: false
  const updatedPortfolioData = {
    ...portfolioData,
    isNotify: false, // Add this line
  };
  const url = `${API_URL}/realtime-portfolio/${portId}`;
  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(updatedPortfolioData),
  });
  if (!res.ok) {
    console.error('Update Portfolio New Error:', res.statusText);
    await handleApiError(res, 'Failed to update portfolio (new)');
  }
  return res.json();
}

export async function queryRealtimePortfolio(options?: {
  keyword?: string;
  owner?: string;
  page?: string;
  limit?: string;
  sort?: string;
  isNotify?: boolean;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const params = new URLSearchParams();
  if (options?.keyword) params.append('keyword', options.keyword);
  if (options?.owner) params.append('owner', options.owner);
  if (options?.page) params.append('page', options.page);
  if (options?.limit) params.append('limit', options.limit);
  if (options?.sort) params.append('sort', options.sort);
  if (options?.isNotify) params.append('isNotify', String(options.isNotify));

  const url = `${API_URL}/realtime-portfolio${params.toString() ? `?${params.toString()}` : ''}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to query realtime portfolio');
  }
  return res.json();
}

export async function importMembersCSV(file: File): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/users/import`;
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      // Do not set Content-Type for FormData; browser will set it automatically
    },
    body: formData,
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to import members CSV');
  }
  return res.json();
}

export async function uploadFile(file: File): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/files/uploads`;
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      // Do not set Content-Type for FormData; browser will set it automatically
    },
    body: formData,
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to upload file');
  }
  return res.json();
}

export async function analyzeAI(payload: {
  symbol: string;
  name: string;
  sector: string;
  industry: string;
  marketCap: number;
  pe: number;
  pbv: number;
  roe: number;
  dividendYield: number;
  price: number;
  priceDate: string;
  financials: Array<{
    year: number;
    revenue: number;
    netProfit: number;
    eps: number;
    bvps: number;
    dps: number;
    roe: number;
    roa: number;
    debtToEquity: number;
    currentRatio: number;
    [key: string]: any;
  }>;
  news: Array<{
    title: string;
    content: string;
    date: string;
    [key: string]: any;
  }>;
  owner: string;
  portfield: string;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/ai/analyze`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to analyze AI');
  }
  return res.json();
}

export async function reorderPort(portIds: string[]): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/realtime-portfolio/reorder`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify({ portIds }),
  });

  if (!res.ok) {
    await handleApiError(res, 'Failed to reorder portfolios');
  }
  return res.json();
}

// Create Group
export async function createGroup(groupData: { name: string; emoji?: string; color?: string }): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/fav/groups`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(groupData),
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to create group');
  }
  return res.json();
}

// List Own Groups
export async function listOwnGroups(): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/fav/groups`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to list own groups');
  }
  return res.json();
}

// Update Group Info
export async function updateGroupInfo(groupId: string, groupData: { name?: string; emoji?: string; color?: string }): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/fav/groups/${groupId}`;
  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(groupData),
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to update group info');
  }
  return res.json();
}

// Set Default Group
export async function setDefaultGroup(groupId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/fav/groups/${groupId}/default`;
  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to set default group');
  }
  return res.json();
}

// Delete Group
export async function deleteGroup(groupId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/fav/groups/${groupId}`;
  const res = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to delete group');
  }
  return res.json();
}

// List Items in Group
export async function listItemsInGroup(groupId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/fav/groups/${groupId}/items`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to list items in group');
  }
  return res.json();
}

// Add Item to Group
// PR #72: หุ้นส่งแบบเดิม { groupId, symbol } ได้ (assetType ไม่ระบุ = STOCK)
// คริปโตต้องส่ง assetType: 'CRYPTO' ทุกครั้ง + assetId จากผลค้น master asset (ไม่งั้น BTC จะถูกบันทึกเป็นหุ้น)
export async function addItemToGroup(itemData: {
  groupId: string;
  symbol?: string;
  assetId?: string;
  assetType?: 'STOCK' | 'CRYPTO';
  note: string;
  alias: string;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/fav/items`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(itemData),
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to add item to group');
  }
  return res.json();
}

// Remove Item from Group
export async function removeItemFromGroup(itemId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/fav/items/${itemId}`;
  const res = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to remove item from group');
  }
  return res.json();
}


// Verify Telegram Link
export async function verifyTelegramLink(payload: {
  id: string;
  first_name: string;
  last_name: string;
  hash: string;
  ref: string;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/telegram-auth/verify`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to verify Telegram link');
  }
  return res.json();
}

// Get Telegram Link Info
export async function getTelegramLinkInfo(options?: {
  startApp?: string;
  ref?: string;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const params = new URLSearchParams();
  if (options?.startApp) params.append('startApp', options.startApp);
  if (options?.ref) params.append('ref', options.ref);

  const url = `${API_URL}/telegram-auth/info?${params.toString()}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to get Telegram link info');
  }
  return res.json();
}

// Generate Telegram Link
export async function generateTelegramLink(): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/telegram-auth/generate-link-url`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to generate Telegram link');
  }
  return res.json();
}

// Send Telegram Broadcast Realtime Portfolio
export async function sendTelegramBroadcastRealtimePortfolio(realtimeId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/broadcast/realtime/${realtimeId}/announce`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (res.status !== 201) {
    await handleApiError(res, 'Failed to send Telegram broadcast for realtime portfolio');
  }

  // แก้ตรงนี้
  const text = await res.text();
  if (!text) {
    return { success: true };
  }
  try {
    return JSON.parse(text);
  } catch {
    return { success: true };
  }
}


// Check Telegram Linked Status
// export async function checkTelegramLinkedStatus(telegramId: string): Promise<any> {
//   // const authToken = getToken();
//   // if (!authToken) throw new Error('No token found');

//   const url = `${API_URL}/telegram/linked/${telegramId}/status`;
//   const res = await fetch(url, {
//     method: 'GET',
//     // headers: {
//     //   'Authorization': `Bearer ${authToken}`,
//     // },
//   });
//   if (!res.ok) {
//     await handleApiError(res, 'Failed to check Telegram linked status');
//   }
//   return res.json();
// }

// Telegram Login with Google
export async function telegramLoginGoogle(telegramId: string, accessToken: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/telegram/linked/${telegramId}/login`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify({ access_token: accessToken }),
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to login Telegram with Google');
  }
  return res.json();
}

// Link Telegram to User
export async function linkTelegramToUser(payload: {
  telegramId: string;
  telegramFirstname: string;
  // telegramLastname: string;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/telegram/linked/verify`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to link Telegram to user');
  }
  return res.json();
}


export async function getTelegramMe(): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/telegram/me`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to fetch Telegram info');
  }
  return res.json();
}

export async function unlinkTelegram(telegramId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');
  const url = `${API_URL}/telegram/${telegramId}/unlink`;
  const res = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to unlink Telegram');
  }
  return res.json();
}

export async function getBenchmarkByPortId(portfolioId: string, timescale: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/benchmark/${portfolioId}?timescale=${timescale}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) {
    await handleApiError(res, 'Failed to get benchmark by port id');
  }
  return res.json();
}
// 1. Benchmark V1
export async function getBenchmarkV1(
  portId: string,
  subPortId: string,
  params?: { timeframe?: string; range?: string; timescale?: string }
): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  // จัดการ Query Parameters
  const queryParams = new URLSearchParams();
  if (params?.timeframe) queryParams.append('timeframe', params.timeframe);
  if (params?.range) queryParams.append('range', params.range);
  if (params?.timescale) queryParams.append('timescale', params.timescale);

  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
  const url = `${API_URL}/benchmark/${portId}/${subPortId}${queryString}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: { 
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    },
  });

  if (!res.ok) await handleApiError(res, 'Failed to fetch Benchmark V1 data');
  return res.json();
}

// 2. Benchmark V2 (Legacy)
export async function getBenchmarkV2Legacy(
  portfolioId: string,
  subPortId: string
): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/benchmarks/portfolios/${portfolioId}/sub-ports/${subPortId}/legacy`;

  const res = await fetch(url, {
    method: 'GET',
    headers: { 
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    },
  });

  if (!res.ok) await handleApiError(res, 'Failed to fetch Benchmark V2 legacy data');
  return res.json();
}

// 2b. Benchmark V2 (Legacy) - Whole Portfolio Overview (no subPortId)
export async function getBenchmarkV2PortfolioLegacy(
  portfolioId: string
): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/benchmarks/portfolios/${portfolioId}/legacy`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    },
  });

  if (!res.ok) await handleApiError(res, 'Failed to fetch Benchmark V2 portfolio legacy data');
  return res.json();
}

// 2c. Benchmark V2 - Catalog (รายการดัชนียอดนิยม)
export async function getBenchmarkCatalog(): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/benchmarks/catalog`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    },
  });

  if (!res.ok) await handleApiError(res, 'Failed to fetch Benchmark catalog');
  return res.json();
}

export interface SleeveComparisonParams {
  symbols: string[];
  range?: string;
  timeframe?: string;
  mode?: string;
  from?: string; // ใช้ตอน range === 'CUSTOM'
  to?: string; // ใช้ตอน range === 'CUSTOM'
  priceMode?: string; // เช่น 'CLOSE_PRICE'
}

// 2d. Benchmark V2 - Sleeve Comparison (Whole Portfolio)
export async function getBenchmarkSleeveComparison(
  portfolioId: string,
  params: SleeveComparisonParams
): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const queryParams = new URLSearchParams();
  if (params.symbols?.length) queryParams.append('symbols', params.symbols.join(','));
  if (params.range) queryParams.append('range', params.range);
  if (params.timeframe) queryParams.append('timeframe', params.timeframe);
  if (params.mode) queryParams.append('mode', params.mode);
  if (params.from) queryParams.append('from', params.from);
  if (params.to) queryParams.append('to', params.to);
  if (params.priceMode) queryParams.append('priceMode', params.priceMode);

  const url = `${API_URL}/v2/benchmarks/portfolios/${portfolioId}/sleeve-comparison?${queryParams.toString()}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    },
  });

  if (!res.ok) await handleApiError(res, 'Failed to fetch Benchmark sleeve comparison data');
  return res.json();
}

// 2e. Benchmark V2 - Sleeve Comparison (Sub-Portfolio)
export async function getBenchmarkSleeveComparisonSubPort(
  portfolioId: string,
  subPortId: string,
  params: SleeveComparisonParams
): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const queryParams = new URLSearchParams();
  if (params.symbols?.length) queryParams.append('symbols', params.symbols.join(','));
  if (params.range) queryParams.append('range', params.range);
  if (params.timeframe) queryParams.append('timeframe', params.timeframe);
  if (params.mode) queryParams.append('mode', params.mode);
  if (params.from) queryParams.append('from', params.from);
  if (params.to) queryParams.append('to', params.to);
  if (params.priceMode) queryParams.append('priceMode', params.priceMode);

  const url = `${API_URL}/v2/benchmarks/portfolios/${portfolioId}/sub-ports/${subPortId}/sleeve-comparison?${queryParams.toString()}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    },
  });

  if (!res.ok) await handleApiError(res, 'Failed to fetch Benchmark sleeve comparison data (sub-port)');
  return res.json();
}
//----------------------------------------------------------------------------------------------------

// Get Portfolio by Symbol (V2)
export async function getPortfolioTwo(symbol: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No auth token');
  const url = `${API_URL}/portfolios/symbol/${symbol}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {

      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) throw new Error('Failed to fetch portfolio by symbol');
  return res.json();
}
//----------------------------------------------------------------------------------------------------

export async function getPortfolioBySymbolAll(symbol: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) {
    throw new Error('No token found');
  }

  const url = `${API_URL}/portfolios/symbol/${symbol}/all`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) {
    await handleApiError(res, 'Failed to fetch portfolio by symbol (all)');
  }

  return res.json();
}
// Create Portfolio (V2)
export async function createPortfolioTwo(data: {
  name: string;
  description?: string;
  expectedRatio: number;
  startDate?: string;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No auth token');
  const url = `${API_URL}/portfolios`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to create portfolio');
  return res.json();
}

// Update Portfolio (V2)
export async function updatePortfolioTwo(id: string, data: {
  name: string;
  description: string;
  expectedRatio: number;
  // startDate: string;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No auth token');
  const url = `${API_URL}/portfolios/${id}`;
  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to update portfolio');
  return res.json();
}

// Delete Portfolio (V2)
export async function deletePortfolioTwo(portId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No auth token');
  const url = `${API_URL}/portfolios/${portId}`;
  const res = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) throw new Error('Failed to delete portfolio');
  return res.json();
}

// List all portfolios for the current user (V2)
export async function getAllPortfoliosTwo(): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No auth token');
  const url = `${API_URL}/portfolios`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) throw new Error('Failed to fetch all portfolios');
  return res.json();
}

// Get [one] detail portfolio (V2)
export async function getPortfolioDetailTwo(id: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No auth token');
  const url = `${API_URL}/portfolios/${id}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) throw new Error('Failed to fetch portfolio detail');
  return res.json();
}

// Create a sub-portfolio (subPort) (V2)
export async function createSubPortfolioTwo(portId: string, data: {
  name: string;
  description?: string;
  expectedRatio: number;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No auth token');
  const url = `${API_URL}/portfolios/${portId}/subports`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to create sub-portfolio');
  return res.json();
}

// Update Sub-Portfolio (V2)
export async function updateSubPortfolioTwo(portId: string, subId: string, data: {
  name: string;
  description?: string;
  expectedRatio: number;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No auth token');
  const url = `${API_URL}/portfolios/${portId}/subports/${subId}`;
  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to update sub-portfolio');
  return res.json();
}

// Delete Sub-Portfolio (V2)
export async function deleteSubPortfolioTwo(portId: string, subId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No auth token');
  const url = `${API_URL}/portfolios/${portId}/subports/${subId}`;
  const res = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) throw new Error('Failed to delete sub-portfolio');
  return res.json();
}

// Add a stock to a sub-port (V2)
export async function addStockToSubPortfolioTwo(
  portId: string,
  subId: string,
  stockData: {
    stockSymbol: string;
    quantity: number;
    avgBuyPrice: number;
    expectedRatio?: number;
  }
): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No auth token');
  const url = `${API_URL}/portfolios/${portId}/subports/${subId}/stocks`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(stockData),
  });
  if (!res.ok) throw new Error('Failed to add stock to sub-portfolio');
  return res.json();
}

// Edit stock in sub-portfolio (V2)
export async function updateStockInSubPortfolioTwo(
  portId: string,
  subId: string,
  stockSymbol: string,
  stockData: {
    stockSymbol: string;
    quantity: number;
    avgBuyPrice: number;
    expectedRatio: number;
  }
): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No auth token');
  const url = `${API_URL}/portfolios/${portId}/subports/${subId}/stocks/${stockSymbol}`;
  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(stockData),
  });
  if (!res.ok) throw new Error('Failed to update stock in sub-portfolio');
  return res.json();
}

// Delete stock in sub-portfolio (V2)
export async function deleteStockFromSubPortfolioTwo(
  portId: string,
  subId: string,
  stockSymbol: string
): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No auth token');
  const url = `${API_URL}/portfolios/${portId}/subports/${subId}/stocks/${stockSymbol}`;
  const res = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) throw new Error('Failed to delete stock from sub-portfolio');
  return res.json();
}

// Change Admin Port Visibility (V2)
export async function updatePortfolioVisibilityTwo(
  id: string,
  visibilityStatus: string
): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No auth token');
  const url = `${API_URL}/portfolios/${id}/visibility`;
  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify({ visibilityStatus }),
  });
  if (!res.ok) throw new Error('Failed to update portfolio visibility');
  return res.json();
}

// Get All Public Port [ad aum port] (V2)
export async function getAllPublicPortTwo(): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No auth token');
  const url = `${API_URL}/portfolios/public/all`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) throw new Error('Failed to fetch all public portfolios');
  return res.json();
}

// [Not Use] Add a top-level stock [not in sub port] (V2)
export async function addStockToPortfolioTwo(
  portId: string,
  stockData: {
    stockSymbol: string;
    quantity: number;
    avgBuyPrice: number;
    expectedRatio: number;
  }
): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No auth token');
  const url = `${API_URL}/portfolios/${portId}/stocks`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(stockData),
  });
  if (!res.ok) throw new Error('Failed to add stock to portfolio');
  return res.json();
}

// Get Portfolios For Management (V2)
export async function getPortfoliosForManagementTwo(): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No auth token');
  const url = `${API_URL}/portfolios/admin/manage`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) throw new Error('Failed to fetch portfolios for management');
  return res.json();
}

// Add endpoint to fetch all public portfolios
export async function getAllAdminPortfolios(): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/portfolios/public/all`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) {
    await handleApiError(res, 'Failed to fetch public portfolios');
  }

  return res.json();
}

export async function checkTelegramLinkedStatus(telegramId: string): Promise<any> {

  const url = `${API_URL}/telegram/linked/${telegramId}/status`;
  const res = await fetch(url, {
    method: 'GET',

  });

  if (!res.ok) {
    await handleApiError(res, 'Failed to check Telegram linked status');
  }

  return res.json();
}

/**
 * Verify Telegram Code
 * @param payload - The payload containing verification data
 */
export async function verifyTelegramCode(payload: {
  telegramFirstname: string;
  // telegramLastname: string;
  telegramId: string;
  code: string;
}): Promise<any> {

  const url = `${API_URL}/telegram/linked/v2/verify`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    await handleApiError(res, 'Failed to verify Telegram code');
  }

  return res.json();
}

/**
 * Generate Telegram Code
 * @param refresh - Whether to refresh the code
 */
export async function generateTelegramCode(refresh: boolean = false): Promise<any> {
  const authToken = localStorage.getItem('token');
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/telegram/linked/v2/generate-code?refresh=${refresh}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });

  if (!res.ok) {
    await handleApiError(res, 'Failed to generate Telegram code');
  }

  return res.json();
}

// Change Admin Port Visibility (V2)
export async function updateAdminPortVisibility(id: string, visibilityStatus: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/portfolios/${id}/visibility`;
  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify({ visibilityStatus }),
  });

  if (!res.ok) {
    await handleApiError(res, 'Failed to update admin port visibility');
  }

  return res.json();
}

// Check Session Status
export async function checkSessionStatus(): Promise<any> {
  const authToken = getToken();
  if (!authToken) {
    throw new Error('No token found');
  }

  const url = `${API_URL}/auth/session/status`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });


  if (!res.ok) {
    // ส่งคืน response object ที่ได้รับมา
    const errorResponse = await res.json();
    throw errorResponse;
  }

  return res.json();
}

//check status import members CSV
export async function checkImportMemberStatus(): Promise<any> {

  const url = `${API_URL}/tools/service-metadata`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!res.ok) {
    // ส่งคืน response object ที่ได้รับมา
    const errorResponse = await res.json();
    throw errorResponse;
  }

  return res.json();
}
// Create Guru
export async function createGuru(guruData: { name: string; description?: string }): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/gurus`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(guruData),
  });

  if (!res.ok) {
    await handleApiError(res, 'Failed to create guru');
  }

  return res.json();
}
// Delete Guru
export async function deleteGuru(guruId: string): Promise<void> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/gurus/${guruId}`;
  const res = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) {
    await handleApiError(res, 'Failed to delete guru');
  }
}
// Create Portfolio for Guru
export async function createPortfolioForGuru(guruId: string, portfolioData: { name: string }): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/gurus/${guruId}/portfolios`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(portfolioData),
  });

  if (!res.ok) {
    await handleApiError(res, 'Failed to create portfolio for guru');
  }

  return res.json();
}
// Delete Portfolio for Guru
export async function deletePortfolioForGuru(guruId: string, portfolioId: string): Promise<void> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/gurus/${guruId}/portfolios/${portfolioId}`;
  const res = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) {
    await handleApiError(res, 'Failed to delete portfolio for guru');
  }
}


// Create Realtime Portfolio for Guru (Aj.Shey)
export async function createRealtimePortfolioAS(
  guruId: string,
  portId: string,
  holdings: Array<{
    ticker: string;
    action: string;
    reason: string;
    batches: Array<any>;
  }>
): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/realtime-v2/guru-as/${guruId}/${portId}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify({ holdings }),
  });

  if (!res.ok) {
    await handleApiError(res, 'Failed to create realtime portfolio for Guru AS');
  }

  return res.json();
}

// Create Realtime Portfolio for Guru (ADDUM)
export async function createRealtimePortfolioADDUM(
  guruId: string,
  portId: string,
  data: any
): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/realtime-v2/guru-ad/${guruId}/${portId}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(data),
  });

  if (!res.ok) {
    await handleApiError(res, 'Failed to create realtime portfolio for Guru ADDUM');
  }

  return res.json();
}

// Create Realtime Portfolio for Guru (Alpha Pick)
export async function createRealtimePortfolioAP(
  guruId: string,
  portId: string,
  data: any
): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/realtime-v2/guru-ap/${guruId}/${portId}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(data),
  });

  if (!res.ok) {
    await handleApiError(res, 'Failed to create realtime portfolio for Guru AP');
  }

  return res.json();
}
// Get Realtime Portfolio Data
export async function getRealtimePortfolio(dateKey?: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const queryParam = dateKey ? `?dateKey=${encodeURIComponent(dateKey)}` : '';
  const url = `${API_URL}/realtime-v2${queryParam}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) {
    await handleApiError(res, 'Failed to fetch realtime portfolio data');
  }

  return res.json();
}
export async function deleteRealtimePortfolio(realtimeId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/realtime-v2/${realtimeId}`;
  const res = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) {
    await handleApiError(res, 'Failed to delete realtime portfolio');
  }

  return res.json();
}

// Get Weekly Realtime Portfolio Data
export async function getWeeklyRealtime(): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/realtime-v2/history/weekly`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) {
    await handleApiError(res, 'Failed to fetch weekly realtime portfolio data');
  }

  return res.json();
}

// Export Signals (Admin Export)
export async function exportSignals(payload: {
  ticker: string;
  guruId: string;
  details: any
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/signals/export`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    await handleApiError(res, 'Failed to export signals');
  }

  return res.json();
}
// User Check Updates — หลังบ้านแยกโหมด STOCK / CRYPTO (ต้องส่ง assetType + syncKey ของโหมดนั้น)
export async function checkSignalUpdates(sync: string, assetType: SignalAssetType = 'STOCK'): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/signals/updates`;

  // ✅ แก้ไขตรงนี้: บังคับให้ sync เป็น String แน่นอน 100% 
  // และถ้าเป็น null/undefined ให้กลายเป็น "" (String ว่าง)
  const payload = {
    assetType,
    sync: String(sync || "")
  };

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json', // 👈 เพิ่ม Content-Type ให้ชัดเจนด้วยครับ
    },
    body: JSON.stringify(payload)
  });

  if (!res.ok) {
    await handleApiError(res, 'Failed to check signal updates');
  }
  return res.json();
}

// User Import Selected (POST with Query Params & Body)
export async function importSelectedSignals(payload: {
  signalIds: string[],
  tickers: string[],
  sync?: string,
  // ห้ามปนหุ้นกับคริปโตในคำขอเดียว (ไม่ระบุ = STOCK)
  assetType?: SignalAssetType
}): Promise<any> {
  const authToken = getToken();
  const url = `${API_URL}/signals/import`;

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ assetType: 'STOCK', ...payload })
  });

  if (!res.ok) {
    await handleApiError(res, 'Failed to import signals');
  }
  return res.json();
}


// ดึงอัปเดตทั้ง 2 โหมด (หุ้น + คริปโต) แล้วรวม signals เป็นลิสต์เดียวให้ UI ใช้เหมือนเดิม
// โหมดไหนพังก็ยังได้ผลอีกโหมด / พังทั้งคู่ค่อยโยน error
export async function checkAllSignalUpdates(syncs: SignalSyncs = EMPTY_SIGNAL_SYNCS): Promise<{ signals: any[] }> {
  const results = await Promise.allSettled(
    SIGNAL_ASSET_TYPES.map((type) => checkSignalUpdates(syncs[type], type))
  );
  const ok = results.filter((r): r is PromiseFulfilledResult<any> => r.status === 'fulfilled');
  if (ok.length === 0) throw (results[0] as PromiseRejectedResult).reason;
  results.forEach((r, i) => {
    if (r.status === 'rejected') console.error(`Failed to check ${SIGNAL_ASSET_TYPES[i]} signal updates:`, r.reason);
  });
  return { signals: ok.flatMap((r) => r.value?.signals || []) };
}

// -----------------------------------------------

// User Import Selected (POST with Query Params & Body)
export async function requestAnalysis(payload: {
  ticker: string
}): Promise<any> {
  const authToken = getToken();
  const url = `${API_URL}/analysis-requests`;

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload)
  });

  if (!res.ok) {
    // ✅ 1. ดึงข้อมูล Error จาก Response ก่อน
    const errorData = await res.json().catch(() => ({}));

    // ✅ 2. สร้าง Custom Error เพื่อส่ง Code ออกไปให้ UI ใช้ต่อ
    const error: any = new Error(errorData.message || 'Failed to request analysis');
    error.code = errorData.error?.code || errorData.code; // ดึง code: 400204 มาเก็บไว้
    error.status = res.status;

    // ถ้ามี handleApiError เดิมอยู่แล้ว แนะนำให้ส่ง error ก้อนนี้ออกไปเลย 
    // หรือ throw error เพื่อให้ catch ใน UI ทำงาน
    throw error;
  }
  return res.json();
}


// User Import Selected (POST with Query Params & Body)
export async function myRequest(): Promise<any> {
  const authToken = getToken();
  const url = `${API_URL}/analysis-requests/me`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!res.ok) {
    await handleApiError(res, 'Failed to request analysis stats');
  }
  return res.json();
}



// User Import Selected (POST with Query Params & Body)
export async function adminAllRequest(options?: {
  page?: string;
  limit?: string;
  status?: string;
  ticker?: string;
  groupBy?: string;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const params = new URLSearchParams();
  if (options?.page) params.append('page', options.page);
  if (options?.limit) params.append('limit', options.limit);
  if (options?.status) params.append('status', options.status);
  if (options?.ticker) params.append('ticker', options.ticker);
  if (options?.groupBy) params.append('groupBy', options.groupBy);

  const url = `${API_URL}/analysis-requests/list${params.toString() ? `?${params.toString()}` : ''}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!res.ok) {
    await handleApiError(res, 'Failed to request analysis');
  }
  return res.json();
}


// User Import Selected (POST with Query Params & Body)
export async function adminRequestStats(hotThreshold?: number): Promise<any> {
  const authToken = getToken();
  const url = `${API_URL}/analysis-requests/stats${hotThreshold ? `?hotThreshold=${hotThreshold}` : ''}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!res.ok) {
    await handleApiError(res, 'Failed to request analysis');
  }
  return res.json();
}

// Get User Info
export async function getTopFiveClip(): Promise<any> {

  const url = `${API_URL}/youtube/latest`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to get top five clip');
  }
  return res.json();
}

// Get User Info
export async function getTopFiveSignal(): Promise<any> {


  const url = `${API_URL}/tools/public-signals`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to get top five signal');
  }
  return res.json();
}

// Search Master Assets (หุ้น หรือ คริปโต)
// CRYPTO: ได้เฉพาะคู่ USD ที่ provider ยืนยันแล้ว ผลลัพธ์มี assetId / providerSymbol (X:BTCUSD) ให้ใช้สร้างรายการ
export async function searchMasterAssets(query?: string, assetType: 'STOCK' | 'CRYPTO' = 'STOCK'): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  // ใช้ URL Object เพื่อจัดการ Query Parameters ได้ง่ายขึ้น
  const url = new URL(`${API_URL}/v2/master-assets/search`);
  url.searchParams.append('assetType', assetType);

  if (query) {
    url.searchParams.append('q', query);
  }

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) {
    await handleApiError(res, 'Failed to fetch master assets');
  }

  return res.json();
}



// ============================================================================
// PORTFOLIO V2 ENDPOINTS
// ============================================================================

// 1. Get All Portfolios (V2) - รองรับ query สำหรับ Summary และ Lifecycle (v1.1, v1.2)
export async function getV2Portfolios(options?: {
  summary?: boolean;
  includeClosed?: boolean;
  includeArchived?: boolean;
  includePendingDelete?: boolean;
  status?: string;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const params = new URLSearchParams();
  if (options?.summary !== undefined) params.append('summary', String(options.summary));
  if (options?.includeArchived !== undefined) params.append('includeArchived', String(options.includeArchived));
  if (options?.includePendingDelete !== undefined) params.append('includePendingDelete', String(options.includePendingDelete));
  if (options?.includeClosed !== undefined) params.append('includeClosed', String(options.includeClosed));
  if (options?.status) params.append('status', options.status);

  const url = `${API_URL}/v2/portfolios${params.toString() ? `?${params.toString()}` : ''}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) await handleApiError(res, 'Failed to fetch V2 portfolios');
  return res.json();
}

// 2. Get Portfolio Detail (V2)
export async function getV2PortfolioDetail(portId: string, options?: {
  view?: string;
  includeLots?: boolean;
  holdingStatus?: string;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const params = new URLSearchParams();
  if (options?.view) params.append('view', options.view);
  if (options?.includeLots !== undefined) params.append('includeLots', String(options.includeLots));
  if (options?.holdingStatus) params.append('holdingStatus', options.holdingStatus);

  const url = `${API_URL}/v2/portfolios/${portId}${params.toString() ? `?${params.toString()}` : ''}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) await handleApiError(res, 'Failed to fetch V2 portfolio detail');
  return res.json();
}

// 3. Create Portfolio (V2)
export async function createV2Portfolio(portfolioData: {
  name: string;
  description?: string;
  portfolioType: string;
  baseCurrency: string;
  expectedRatio: number;       // ✅ ใส่กลับเข้ามาให้ User กรอก
  cashExpectedRatio: number;   // ✅ คงไว้เพื่อส่งค่า 0 ตาม Spec
  visibilityStatus?: string;
  createDefaultSubPort?: boolean;
  defaultSubPort?: { name: string; description?: string; expectedRatio: number; };
  settings?: any;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(portfolioData),
  });

  if (!res.ok) await handleApiError(res, 'Failed to create V2 portfolio');
  return res.json();
}

// 4. Update Portfolio Allocation (V2)
export async function updateV2PortfolioAllocation(portId: string, allocationData: {
  cashExpectedRatio: number;
  subPorts: Array<{ subPortId: string; expectedRatio: number; }>;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/allocation`;
  const res = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(allocationData),
  });

  if (!res.ok) await handleApiError(res, 'Failed to update V2 portfolio allocation');
  return res.json();
}

// ============================================================================
// SUBPORT & HOLDINGS V2
// ============================================================================

// 5. Create Subport (V2)
export async function createV2Subport(portId: string, subportData: {
  name: string;
  description?: string;
  expectedRatio: number;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/subports`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(subportData),
  });

  if (!res.ok) await handleApiError(res, 'Failed to create V2 subport');
  return res.json();
}

// 6. Get Holdings by Symbol across all portfolios (v1.1)
export async function getV2HoldingsBySymbol(symbol: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/holdings/by-symbol/${symbol}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) await handleApiError(res, 'Failed to fetch holdings by symbol');
  return res.json();
}

// ============================================================================
// TRANSACTIONS V2
// ============================================================================

export interface CryptoDecimalValues {
  quantity?: string;
  price?: string;
  amount?: string;
  fee?: string;
  tax?: string;
  targetQuantity?: string;
  targetAvgPrice?: string;
  targetTotalCost?: string;
}

// 7. Create V2 Transaction
export async function createV2Transaction(portId: string, transactionData: {
  type: string;
  subPortId?: string;
  assetId?: string;
  assetSymbol?: string;
  quantity?: number;
  price?: number;
  amount?: number;
  grossAmount?: number;
  fee?: number;
  tax?: number;
  cashCurrency?: string;
  transactionDate?: string;
  note?: string;
  targetQuantity?: number;
  source?: string;
  targetAvgPrice?: number;
  adjustmentReason?: string;
  idempotencyKey?: string;
  mode?: string; // e.g., 'REALTIME', 'REPLAY'
  metadata?: any;
  // พอร์ต CRYPTO: ตัวเลขบัญชีส่งเป็น string ตรงตัว (ทศนิยมไม่เกิน 36 ตำแหน่ง) ห้ามแปลงเป็น Number
  decimalValues?: CryptoDecimalValues;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/transactions`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(transactionData),
  });

  if (!res.ok) await handleApiError(res, 'Failed to create V2 transaction');
  return res.json();
}

// 8. Rebuild Portfolio Snapshot (V2)
export async function rebuildV2Portfolio(portId: string, dryRun: boolean = false): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/rebuild`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify({ dryRun }),
  });

  if (!res.ok) await handleApiError(res, 'Failed to rebuild V2 portfolio');
  return res.json();
}

// ============================================================================
// LIFECYCLE & MOVE (v1.2)
// ============================================================================

// 9. Clone Portfolio (v1.2)
export async function cloneV2Portfolio(portId: string, cloneData: {
  name: string;
  mode: string; // e.g., 'REPLAY_TRANSACTIONS'
  costBasisMethod: string;
  compressionStrategy?: string;
  rebuild?: { runImmediately?: boolean; runReconcile?: boolean; };
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/clone`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(cloneData),
  });

  if (!res.ok) await handleApiError(res, 'Failed to clone V2 portfolio');
  return res.json();
}

// 10. Archive Portfolio (v1.2)
export async function archiveV2Portfolio(portId: string, reason: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/archive`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify({ reason }),
  });

  if (!res.ok) await handleApiError(res, 'Failed to archive V2 portfolio');
  return res.json();
}

// 11. Move Holding (v1.2)
export async function moveV2Holding(portId: string, moveData: {
  fromSubPortId: string;
  toSubPortId: string;
  assetSymbol?: string;
  assetId?: string;
  moveMode: string;
  quantity?: number;
  lotSelection?: any;
  note?: string;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/holdings/move`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(moveData),
  });

  if (!res.ok) await handleApiError(res, 'Failed to move holding in V2 portfolio');
  return res.json();
}

// ============================================================================
// CORPORATE ACTIONS / DIVIDEND (v1.3)
// ============================================================================

// 12. Get Portfolio Corporate Actions List (v1.3)
export async function getV2PortfolioCorporateActions(portId: string, options?: {
  symbol?: string;
  settlementStatus?: string;
  from?: string;
  to?: string;
  limit?: string;
  cursor?: string;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const params = new URLSearchParams();
  if (options?.symbol) params.append('symbol', options.symbol);
  if (options?.settlementStatus) params.append('settlementStatus', options.settlementStatus);
  if (options?.limit) params.append('limit', options.limit);
  if (options?.cursor) params.append('cursor', options.cursor);

  const url = `${API_URL}/v2/portfolios/${portId}/corporate-actions${params.toString() ? `?${params.toString()}` : ''}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) await handleApiError(res, 'Failed to fetch portfolio corporate actions');
  return res.json();
}

// 13. Settle Dividend Transaction (v1.3)
export async function settleV2CorporateAction(portId: string, transactionId: string, force: boolean = false): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/corporate-actions/${transactionId}/settle`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify({ force }),
  });

  if (!res.ok) await handleApiError(res, 'Failed to settle corporate action');
  return res.json();
}

// 14. Void Dividend Transaction (v1.3)
export async function voidV2CorporateAction(portId: string, transactionId: string, reason: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/corporate-actions/${transactionId}/void`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify({ reason }),
  });

  if (!res.ok) await handleApiError(res, 'Failed to void corporate action');
  return res.json();
}


// ============================================================================
// PORTFOLIO V2 - ADDITIONAL CORE & SETTINGS (จาก 1.0 และ 1.2)
// ============================================================================

// Get Cost Basis Presets
export async function getV2CostBasisPresets(): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/cost-basis-presets`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${authToken}` },
  });
  if (!res.ok) await handleApiError(res, 'Failed to fetch cost basis presets');
  return res.json();
}

// Update Portfolio Info (Name, Description, etc.)
export async function updateV2PortfolioInfo(
  portId: string,
  data: { name?: string; description?: string; expectedRatio?: number } // ✅ เพิ่ม expectedRatio ตรงนี้
): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}`;
  const res = await fetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
    body: JSON.stringify(data),
  });
  if (!res.ok) await handleApiError(res, 'Failed to update portfolio info');
  return res.json();
}

// Update Portfolio Settings
export async function updateV2PortfolioSettings(portId: string, settingsData: any): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/settings`;
  const res = await fetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
    body: JSON.stringify(settingsData),
  });
  if (!res.ok) await handleApiError(res, 'Failed to update portfolio settings');
  return res.json();
}

// Update Holding Allocation in Subport
export async function updateV2HoldingAllocation(portId: string, subPortId: string, holdingsData: Array<{ assetId: string; expectedRatio: number }>): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/subports/${subPortId}/holdings/allocation`;
  const res = await fetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
    body: JSON.stringify({ holdings: holdingsData }),
  });
  if (!res.ok) await handleApiError(res, 'Failed to update holding allocation');
  return res.json();
}

// Request Portfolio Delete (PENDING_DELETE)
export async function requestDeleteV2Portfolio(portId: string, reason?: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/delete`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
    body: JSON.stringify({ reason }),
  });
  if (!res.ok) await handleApiError(res, 'Failed to request portfolio delete');
  return res.json();
}

// Restore Portfolio
export async function restoreV2Portfolio(portId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/restore`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${authToken}` },
  });
  if (!res.ok) await handleApiError(res, 'Failed to restore portfolio');
  return res.json();
}

// ============================================================================
// SUBPORT & HOLDINGS - ADDITIONAL ACTIONS (จาก 1.0 และ 1.2)
// ============================================================================

// Get Subport Detail
export async function getV2SubportDetail(portId: string, subPortId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/subports/${subPortId}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${authToken}` },
  });
  if (!res.ok) await handleApiError(res, 'Failed to fetch subport detail');
  return res.json();
}

// Update Subport Info
export async function updateV2Subport(portId: string, subPortId: string, data: { name?: string; description?: string; expectedRatio?: number }): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/subports/${subPortId}`;
  const res = await fetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
    body: JSON.stringify(data),
  });
  if (!res.ok) await handleApiError(res, 'Failed to update subport');
  return res.json();
}

// Archive Subport
export async function archiveV2Subport(portId: string, subPortId: string, resetAllocation?: boolean): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/subports/${subPortId}/archive`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
    body: JSON.stringify({ resetAllocation }),
  });
  if (!res.ok) await handleApiError(res, 'Failed to archive subport');
  return res.json();
}

// Request Delete Subport
export async function requestDeleteV2Subport(portId: string, subPortId: string, reason: string = 'User deleted', resetAllocation?: boolean): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/subports/${subPortId}/delete`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
    body: JSON.stringify({ reason, resetAllocation }),
  });
  if (!res.ok) await handleApiError(res, 'Failed to request subport delete');
  return res.json();
}

// Restore Subport
export async function restoreV2Subport(portId: string, subPortId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/subports/${subPortId}/restore`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${authToken}` },
  });
  if (!res.ok) await handleApiError(res, 'Failed to restore subport');
  return res.json();
}

// Merge Subports
export async function mergeV2Subports(portId: string, mergeData: {
  targetSubPortId: string;
  sourceSubPortIds: string[];
  mode: string;
  archiveSources?: boolean;
  allocationStrategy?: string;
  dryRun?: boolean;
  reason: string;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/subports/merge`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
    body: JSON.stringify(mergeData),
  });
  if (!res.ok) await handleApiError(res, 'Failed to merge subports');
  return res.json();
}

// Delete/Close Holding (HIDE_IF_CLOSED, ADJUST_TO_ZERO, SELL_ALL)
export async function deleteV2Holding(portId: string, subPortId: string, assetId: string, modeData: any): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/subports/${subPortId}/holdings/${assetId}/delete`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
    body: JSON.stringify(modeData), // e.g. { mode: 'HIDE_IF_CLOSED' }
  });
  if (!res.ok) await handleApiError(res, 'Failed to delete or close holding');
  return res.json();
}

// Move All Holdings
export async function moveAllV2Holdings(portId: string, sourceSubPortId: string, moveAllData: {
  targetSubPortId: string;
  assetIds?: string[];
  archiveSourceSubPort?: boolean;
  allocationStrategy?: string;
  transactionDate?: string;
  note?: string;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/subports/${sourceSubPortId}/holdings/move-all`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
    body: JSON.stringify(moveAllData),
  });
  if (!res.ok) await handleApiError(res, 'Failed to move all holdings');
  return res.json();
}

// Get Holding Lots
export async function getV2HoldingLots(portId: string, subPortId: string, holdingId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/subports/${subPortId}/holdings/${holdingId}/lots`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${authToken}` },
  });
  if (!res.ok) await handleApiError(res, 'Failed to fetch holding lots');
  return res.json();
}

// ============================================================================
// TRANSACTIONS - ADDITIONAL (จาก 1.0)
// ============================================================================

// Get Transactions (Cursor Pagination)
export async function getV2Transactions(portId: string, options?: {
  limit?: string;
  cursor?: string;
  status?: string;
  type?: string;
  assetId?: string;
  subPortId?: string;
  dateFrom?: string;
  dateTo?: string;
  sortOrder?: string;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const params = new URLSearchParams();
  if (options?.limit) params.append('limit', options.limit);
  if (options?.cursor) params.append('cursor', options.cursor);
  if (options?.status) params.append('status', options.status);
  if (options?.type) params.append('type', options.type);
  if (options?.assetId) params.append('assetId', options.assetId);
  if (options?.subPortId) params.append('subPortId', options.subPortId);
  if (options?.dateFrom) params.append('dateFrom', options.dateFrom);
  if (options?.dateTo) params.append('dateTo', options.dateTo);
  if (options?.sortOrder) params.append('sortOrder', options.sortOrder);

  const url = `${API_URL}/v2/portfolios/${portId}/transactions${params.toString() ? `?${params.toString()}` : ''}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${authToken}` },
  });
  if (!res.ok) await handleApiError(res, 'Failed to fetch transactions');
  return res.json();
}

// Get Single Transaction
export async function getV2TransactionById(portId: string, transactionId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/transactions/${transactionId}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${authToken}` },
  });
  if (!res.ok) await handleApiError(res, 'Failed to fetch transaction detail');
  return res.json();
}

// Bulk Preview Transactions
export async function previewV2BulkTransactions(portId: string, items: any[]): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/transactions/bulk/preview`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
    body: JSON.stringify({ items }),
  });

  // 1. ดึงข้อมูล JSON ออกมาเสมอ ไม่ว่าผลจะเป็นอย่างไร
  const data = await res.json();

  // 2. ถ้าไม่สำเร็จ ให้โยน Error ที่พ่วงข้อมูล data ออกไปด้วย
  if (!res.ok) {
    const error: any = new Error('Failed to preview bulk transactions');
    error.response = { data }; // ใส่ข้อมูล data ลงไปใน error object เพื่อเอาไปใช้ต่อใน catch
    throw error;
  }

  return data;
}

// Bulk Create Transactions
export async function createV2BulkTransactions(portId: string, items: any[], mode?: string, source?: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/transactions/bulk`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
    body: JSON.stringify({
      items,
      ...(mode && { mode }),
      ...(source && { source })
    }),
  });
  if (!res.ok) await handleApiError(res, 'Failed to create bulk transactions');
  return res.json();
}

// Patch Transaction (Update Note/Financials)
export async function updateV2Transaction(portId: string, transactionId: string, data: any): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/transactions/${transactionId}`;
  const res = await fetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
    body: JSON.stringify(data),
  });
  if (!res.ok) await handleApiError(res, 'Failed to update transaction');
  return res.json();
}

// Void User Transaction
export async function voidV2Transaction(portId: string, transactionId: string, reason: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/transactions/${transactionId}/void`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
    body: JSON.stringify({ reason }),
  });
  if (!res.ok) await handleApiError(res, 'Failed to void transaction');
  return res.json();
}

// Reconcile Portfolio (Dry Run Rebuild)
export async function reconcileV2Portfolio(portId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/reconcile`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${authToken}` }, // บังคับ dryRun=true จาก backend
  });
  if (!res.ok) await handleApiError(res, 'Failed to reconcile portfolio');
  return res.json();
}

// Get Reconciliation Logs
export async function getV2ReconciliationLogs(portId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/reconciliation-logs`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${authToken}` },
  });
  if (!res.ok) await handleApiError(res, 'Failed to fetch reconciliation logs');
  return res.json();
}

// ============================================================================
// ADMIN ENDPOINTS (PURGE, PUBLIC PORTFOLIOS, CORPORATE ACTIONS)
// ============================================================================

// Get Public Admin Portfolios
export async function getV2PublicAdminPortfolios(): Promise<any> {
  const authToken = getToken();
  const url = `${API_URL}/v2/portfolios/public`;
  const res = await fetch(url, {
    method: 'GET',
    headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {}, // อาจไม่ต้องใช้ Auth ตามเอกสาร
  });
  if (!res.ok) await handleApiError(res, 'Failed to fetch public admin portfolios');
  return res.json();
}

// Get Public Admin Portfolio Detail
export async function getPublicV2PortfolioDetail(portId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  // ⚠️ ลองเช็ค URL นี้กับเอกสาร API ของคุณดูนะครับว่าตรงไหม
  const url = `${API_URL}/v2/portfolios/public/${portId}`;
  // หรืออาจจะเป็น `${API_URL}/v2/admin/portfolios/${portId}`

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) await handleApiError(res, 'Failed to fetch public portfolio');
  const json = await res.json();
  return json.data;
}

// Update Visibility (Admin Only)
export async function updateV2PortfolioVisibility(portId: string, visibilityStatus: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/visibility`;
  const res = await fetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
    body: JSON.stringify({ visibilityStatus }),
  });
  if (!res.ok) await handleApiError(res, 'Failed to update portfolio visibility');
  return res.json();
}

// Update Public View Settings (Admin Only)
export async function updateV2PublicViewSettings(portId: string, publicView: any): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/public-view-settings`;
  const res = await fetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
    body: JSON.stringify(publicView),
  });
  if (!res.ok) await handleApiError(res, 'Failed to update public view settings');
  return res.json();
}

// Purge Portfolio (Admin Only)
export async function purgeV2PortfolioAdmin(portId: string, confirmationData: { dryRun?: boolean; confirmationToken?: string; reason?: string }): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin/portfolios/${portId}/purge`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
    body: JSON.stringify(confirmationData),
  });
  if (!res.ok) await handleApiError(res, 'Failed to purge portfolio');
  return res.json();
}

// Purge Subport (Admin Only)
export async function purgeV2SubportAdmin(portId: string, subPortId: string, confirmationData: { dryRun?: boolean; confirmationToken?: string; reason?: string }): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin/portfolios/${portId}/subports/${subPortId}/purge`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
    body: JSON.stringify(confirmationData),
  });
  if (!res.ok) await handleApiError(res, 'Failed to purge subport');
  return res.json();
}

// ============================================================================
// CORPORATE ACTIONS / DIVIDEND - GLOBAL & ADMIN (จาก 1.3)
// ============================================================================

// Get Global Corporate Action Dividends by Symbol
export async function getV2GlobalCorporateActionsBySymbol(symbol: string, options?: {
  from?: string;
  to?: string;
  status?: string;
  limit?: string;
  cursor?: string;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const params = new URLSearchParams();
  if (options?.from) params.append('from', options.from);
  if (options?.to) params.append('to', options.to);
  if (options?.status) params.append('status', options.status);
  if (options?.limit) params.append('limit', options.limit);
  if (options?.cursor) params.append('cursor', options.cursor);

  const url = `${API_URL}/v2/corporate-actions/dividends/symbol/${symbol}${params.toString() ? `?${params.toString()}` : ''}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${authToken}` },
  });
  if (!res.ok) await handleApiError(res, 'Failed to fetch global corporate actions');
  return res.json();
}

// Admin: Sync Active Tickers (Under Development / Design Review)
export async function syncV2CorporateActionsAdmin(data: any): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin/corporate-actions/dividends/sync-active-tickers`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
    body: JSON.stringify(data),
  });
  if (!res.ok) await handleApiError(res, 'Failed to sync corporate actions');
  return res.json();
}

// Admin: Backfill Active Tickers (Under Development / Design Review)
export async function backfillV2CorporateActionsAdmin(data: any): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin/corporate-actions/dividends/backfill-active-tickers`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
    body: JSON.stringify(data),
  });
  if (!res.ok) await handleApiError(res, 'Failed to backfill corporate actions');
  return res.json();
}

// Admin: Apply Corporate Action (Under Development / Design Review)
export async function applyV2CorporateActionAdmin(corporateActionId: string, data: any): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin/corporate-actions/${corporateActionId}/apply`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
    body: JSON.stringify(data),
  });
  if (!res.ok) await handleApiError(res, 'Failed to apply corporate action');
  return res.json();
}

// Admin: Settle Corporate Action (Under Development / Design Review)
export async function settleV2CorporateActionAdmin(corporateActionId: string, data: any): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin/corporate-actions/${corporateActionId}/settle`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
    body: JSON.stringify(data),
  });
  if (!res.ok) await handleApiError(res, 'Failed to settle corporate action as admin');
  return res.json();
}

// สร้าง Transaction ฝาก/ถอนเงิน
export async function manageV2CashTransaction(
  portId: string,
  actionType: 'CASH_DEPOSIT' | 'CASH_WITHDRAW' | 'CASH_ADJUST',
  amount: number,
  note: string = '',
  transactionDate: string // ✅ รับวันที่เข้ามา
): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/transactions`;

  // ✅ 1. แปลงสถานะ actionType ให้ตรงกับสเปกหลังบ้าน (ถ้า DEPOSIT/WITHDRAWAL ยัง Error ลองสลับใช้คู่ล่างดูครับ)
  // const mappedType = actionType === 'DEPOSIT' ? 'CASH_DEPOSIT' : 'CASH_WITHDRAW'; 
  // หมายเหตุ: หาก Backend ยัง Error เรื่อง type อยู่ ให้ลองเปลี่ยนกลับเป็น actionType ตัวใหญ่หรือตัวเล็กดูครับ
  // const date = new Date();
  // date.setFullYear(date.getFullYear() - 10); // ลบปีออก 10 ปี
  // const isoString = date.toISOString();
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
    body: JSON.stringify({
      type: actionType,                         // ✅ ส่งค่าที่ map แล้ว
      amount: amount,
      note: note,
      transactionDate    // ❌ ลบฟิลด์ currency: "USD" ออกไปแล้วตามคำสั่งของหลังบ้าน (should not exist)
    }),
  });

  if (!res.ok) await handleApiError(res, 'Failed to process cash transaction');
  return res.json();
}


// ============================================================================
// CORPORATE ACTIONS / DIVIDEND - BULK SETTLE
// ============================================================================

export type SettleMode = 'SELECTED' | 'BY_TICKER' | 'BY_FILTER';
export type SettlementStatus = 'PENDING' | 'SETTLED' | 'CANCELLED' | 'FAILED';

export interface BulkSettleDividendsPayload {
  mode: SettleMode;
  transactionIds?: string[]; // <= 100 items
  symbol?: string;
  settlementStatus?: SettlementStatus;
  from?: string;
  to?: string;
  dryRun?: boolean;
  force?: boolean;
  reason?: string;
  limit?: string;
}

// Bulk settle corporate-action dividend transactions
export async function bulkSettleV2CorporateActionDividends(
  portId: string,
  payload: BulkSettleDividendsPayload
): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portId}/corporate-actions/dividends/settle`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) await handleApiError(res, 'Failed to bulk settle corporate action dividends');
  return res.json();
}

export async function patchV2TransactionNote(portId: string, txnId: string, note: string): Promise<any> {
  const authToken = getToken();
  const url = `${API_URL}/v2/portfolios/${portId}/transactions/${txnId}`;
  const res = await fetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
    body: JSON.stringify({ note }), // ส่งแก้แค่ note จะไม่กระทบ snapshot
  });
  if (!res.ok) throw new Error('Failed to update note');
  return res.json();
}


export const previewCostBasisChange = async (portfolioId: string, config: { preset: string, method: string, compressionStrategy: string }, reason: string) => {
  const authToken = getToken();

  const response = await fetch(`${API_URL}/v2/portfolios/${portfolioId}/cost-basis/preview`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}` // ถ้าโปรเจกต์คุณมี Interceptor จัดการให้อยู่แล้ว ไม่ต้องใส่
    },
    body: JSON.stringify({ costBasisConfig: config, reason })
  });

  if (!response.ok) throw new Error('Failed to preview cost basis change');
  return response.json();
};

export const applyCostBasisChange = async (portfolioId: string, config: { preset: string, method: string, compressionStrategy: string }, confirmToken: string, reason: string) => {
  const authToken = getToken();

  const response = await fetch(`${API_URL}/v2/portfolios/${portfolioId}/cost-basis/change`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}` // ถ้าโปรเจกต์คุณมี Interceptor จัดการให้อยู่แล้ว ไม่ต้องใส่

    },
    body: JSON.stringify({
      costBasisConfig: config,
      confirmToken: confirmToken,
      reason: reason
    })
  });

  if (!response.ok) throw new Error('Failed to apply cost basis change');
  return response.json();
};


export const previewCsvTransactions = async (
  portfolioId: string,
  file: File,
  subPortId: string,
  autoCashMode: string,
  // เพิ่ม parameters สำหรับ field ที่ขาดไป
  broker: string = 'M1',
  sourceDataKind: string = 'ADJUSTED_TAX_LOT',
  importMode: string = 'RECONSTRUCT_RAW_FROM_ADJUSTED_TAX_LOT',
  corporateActionPolicy: string = '' // ✅ เพิ่มพารามิเตอร์นี้
) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('subPortId', subPortId);
  formData.append('autoCashMode', autoCashMode);
  // ✅ เพิ่มค่าที่เหลือตาม curl
  formData.append('broker', broker);
  formData.append('sourceDataKind', sourceDataKind);
  formData.append('corporateActionPolicy', corporateActionPolicy); // ส่งค่าจากพารามิเตอร์
  formData.append('importMode', importMode);
  const authToken = getToken();
  const response = await fetch(`${API_URL}/v2/portfolios/${portfolioId}/transactions/import/csv`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authToken}`
    },
    body: formData,
  });

  // อ่าน JSON ออกมาเพื่อดูรายละเอียด Error
  const result = await response.json();

  if (!response.ok) {
    // โยน Error ที่มี Object ของข้อความส่งกลับไป
    const error = new Error(result.message || 'Failed to preview CSV import');
    (error as any).response = { data: result }; // แนบข้อมูล Error จาก Backend ไปด้วย
    throw error;
  }

  return result;
};

// นำเข้า CSV ของพอร์ตคริปโต (หลังบ้านเลือก parser CRYPTO_USD ตามชนิดพอร์ตเอง)
// ส่งแค่ file + subPortId + autoCashMode=NONE ตามคู่มือ — ไม่ส่ง broker/importMode ของพอร์ตหุ้น
export const previewCryptoCsvTransactions = async (portfolioId: string, file: File, subPortId: string) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('subPortId', subPortId);
  formData.append('autoCashMode', 'NONE');
  const response = await fetch(`${API_URL}/v2/portfolios/${portfolioId}/transactions/import/csv`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${getToken()}` },
    body: formData,
  });
  if (!response.ok) await handleApiError(response, 'Failed to preview crypto CSV import');
  return response.json();
};

export const batchMoveHoldings = async (portId: string, moveData: {
  note: string;
  transactionDate: string;
  items: {
    fromSubPortId: string;
    toSubPortId: string;
    assetSymbol: string;
    moveMode: 'QUANTITY' | 'ALL';
    quantity?: number;
  }[];
}) => {

  const res = await fetch(`${API_URL}/v2/portfolios/${portId}/holdings/move/batch`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${getToken()}`
    },
    body: JSON.stringify(moveData),
  });

  if (!res.ok) await handleApiError(res, 'Failed to batch move holdings');
  return res.json();
};

export const updatePortfolioDisplayMode = async (mode: string) => {
  const authToken = getToken();
  const response = await fetch(`${API_URL}/users/me/portfolio-display-mode`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify({ mode }),
  });

  if (!response.ok) {
    throw new Error(`Failed to update display mode: ${response.statusText}`);
  }

  return await response.json();
};

// ในไฟล์ fetchAPI.ts
export const manageCashV1 = async (portId: string, data: {
  amount: number;
  cashCurrency: string;
  expectedRatio: any;
  note?: string;
  type: 'CASH_ADJUST' | 'CASH_DEPOSIT' | 'CASH_WITHDRAW'
}) => {
  const authToken = getToken();
  const response = await fetch(`${API_URL}/portfolios/${portId}/cash`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}` // อย่าลืมใส่ Token
    },
    body: JSON.stringify({
      ...data,
      note: data.note || '-'
    }),
  });

  if (!response.ok) throw new Error('Failed to manage cash');
  return response.json();
};


// สำหรับอัปเดต Realized Gain
export const updateRealizedGainV1 = async (
  portId: string,
  subPortId: string,
  stockSymbol: string,
  data: { realizedGain: number; action: 'overwrite' | 'add' | 'subtract' }
) => {
  const authToken = getToken();
  const response = await fetch(`${API_URL}/portfolios/${portId}/subports/${subPortId}/stocks/${stockSymbol}/realized-gain`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}` // อย่าลืมใส่ Token
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) throw new Error('Failed to update realized gain');
  return response.json();
};

// สำหรับบันทึก Dividend
export const addDividendV1 = async (
  portId: string,
  subPortId: string,
  stockSymbol: string,
  data: { dividend: number; action: 'overwrite' | 'add' | 'subtract' }
) => {
  const authToken = getToken();
  const response = await fetch(`${API_URL}/portfolios/${portId}/subports/${subPortId}/stocks/${stockSymbol}/dividend`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) throw new Error('Failed to add dividend');
  return response.json();
};

export const batchUpdatePortfolioRatios = async (portfolios: { portfolioId: string, expectedRatio: number }[]) => {
  const authToken = getToken();
  const response = await fetch(`${API_URL}/portfolios/expected-ratios`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
    body: JSON.stringify({ portfolios }),
  });
  if (!response.ok) throw new Error('Failed to batch update ratios');
  return response.json();
};


export const updateSubPortfolioAllocationV1 = async (
  portId: string,
  data: {
    cashExpectedRatio: number;
    subPorts: { subPortId: string; expectedRatio: number }[];
  }
) => {
  const authToken = getToken();
  const response = await fetch(`${API_URL}/portfolios/${portId}/allocation`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    // ตามเอกสาร หน้า 6 มี Error Matrix สำหรับกรณีสัดส่วนเกิน
    throw new Error('Failed to update portfolio allocation');
  }
  return response.json();
};

export const updateHoldingAllocationV1 = async (
  portId: string,
  subId: string,
  holdings: { assetId?: string; expectedRatio: number }[]
) => {
  const authToken = getToken();
  const response = await fetch(`${API_URL}/portfolios/${portId}/subports/${subId}/holdings/allocation`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`
    },
    body: JSON.stringify({ holdings }),
  });
  if (!response.ok) throw new Error('Failed to update holding allocation');
  return response.json();
};

export const moveHoldingV1 = async (
  portfolioId: string,
  sourceSubPortId: string | null, // null ถ้าต้นทางเป็น Root
  stockSymbol: string,
  targetSubPortId: string | null // null ถ้าปลายทางเป็น Root
) => {
  const authToken = getToken();
  // เลือก Path ตามเอกสาร: 
  // ถ้า source เป็น sub-port: /portfolios/:id/subports/:subId/stocks/:stockSymbol/move
  // ถ้า source เป็น root: /portfolios/:id/stocks/:stockSymbol/move
  const path = sourceSubPortId
    ? `/portfolios/${portfolioId}/subports/${sourceSubPortId}/stocks/${stockSymbol}/move`
    : `/portfolios/${portfolioId}/stocks/${stockSymbol}/move`;

  const response = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`
    },
    body: JSON.stringify({ targetSubPortId }), // null ได้ถ้าปลายทางคือ Root
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})); // พยายามอ่าน JSON ถ้าไม่ได้ให้เป็น object ว่าง
    const errorMessage = errorData.message || errorData.error?.message || 'Failed to move stock in V1';
    throw new Error(errorMessage); // ส่งข้อความจริงๆ ออกไปให้ catch ใน Modal
  } return response.json();
};


export async function getImportJobStatus(portId: string, jobId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  // อย่าลืมแก้ URL ให้มีทั้ง portId และ jobId ด้วยนะครับ
  const url = `${API_URL}/v2/portfolios/${portId}/transactions/import-jobs/${jobId}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`
    },
  });

  if (!res.ok) await handleApiError(res, 'Failed to fetch import job status');
  return res.json();
}

export const requestRebuildPortfolio = async (portfolioId: string) => {
  const authToken = getToken(); // ดึง Token ตามวิธีที่คุณใช้ปกติ

  const response = await fetch(`${API_URL}/v2/portfolios/${portfolioId}/rebuild`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    },
  });

  const result = await response.json();

  if (!response.ok) {
    const error = new Error(result.message || 'Failed to rebuild portfolio');
    (error as any).response = { data: result };
    throw error;
  }

  return result;
};

export const getCorporateActionSplits = async (
  tickers: string[],
  from: string,
  to: string,
  statuses: string[] = ["READY", "RECON"]
) => {
  const authToken = getToken();

  const response = await fetch(`${API_URL}/v2/corporate-actions/splits/by-tickers`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`
    },
    body: JSON.stringify({
      tickers,
      from,
      to,
      statuses
    }),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.message || 'Failed to fetch corporate action splits');
  }

  return result;
};

export const updatePortfolioThemeV1 = async (portfolioId: string, themeConfig: string) => {
  try {
    const authToken = getToken();

    const response = await fetch(`${API_URL}/portfolios/${portfolioId}/theme-config`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        // หากมี Token สำหรับ Authorization สามารถแนบเพิ่มตรงนี้ได้ เช่น:
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({ themeConfig }),
    });

    if (!response.ok) {
      throw new Error('Failed to update V1 portfolio theme');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating V1 portfolio theme:', error);
    throw error;
  }
};

// ✅ สำหรับพอร์ต V2: PATCH /v2/portfolios/{id}/theme-config
export const updatePortfolioThemeV2 = async (portfolioId: string, themeConfig: string) => {
  try {
    const authToken = getToken();

    const response = await fetch(`${API_URL}/v2/portfolios/${portfolioId}/theme-config`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({ themeConfig }),
    });

    if (!response.ok) {
      throw new Error('Failed to update V2 portfolio theme');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating V2 portfolio theme:', error);
    throw error;
  }
};



// ==================== ADMIN SHAY PORTFOLIO API ====================

// 1. Create Admin Portfolio (POST)
export async function createAdminPortfolio(portfolioData: {
  adminPortfolioId: string;
  slug: string;
  name: string;
  description: string;
  portfolioType: string;
  baseCurrency: string;
  visibilityStatus: string;
  ownerDisplay?: { name: string; title: string };
  publicView?: { showCashBalance: boolean; showAverageCost: boolean; showTotalReturn: boolean, showTransactionHistory: boolean };
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin-portfolios`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(portfolioData),
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to create admin portfolio');
  }
  return res.json();
}

// 2. List Admin Portfolios (GET)
export async function listAdminPortfolios(): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin-portfolios`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to list admin portfolios');
  }
  return res.json();
}

// 3. Get Admin Portfolio by ID (GET)
export async function getAdminPortfolioById(adminPortfolioId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin-portfolios/${adminPortfolioId}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to get admin portfolio');
  }
  return res.json();
}

// 4. Update Admin Portfolio (PATCH)
export async function updateAdminPortfolio(adminPortfolioId: string, updateData: {
  description?: string;
  visibilityStatus?: string;
  [key: string]: any;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin-portfolios/${adminPortfolioId}`;
  const res = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(updateData),
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to update admin portfolio');
  }
  return res.json();
}

// 5. Archive Admin Portfolio (POST)
export async function archiveAdminPortfolio(adminPortfolioId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin-portfolios/${adminPortfolioId}/archive`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to archive admin portfolio');
  }
  return res.json();
}

// ==================== IMPORT AND PUBLISH ====================

// 6. Preview Reset Snapshot (POST)
export async function previewResetSnapshot(adminPortfolioId: string, previewData: {
  mode: string;
  cashBalance: number;
  asOfDate: string;
  timezone: string;
  remark: string;
  idempotencyKey: string;
  items: Array<{
    ticker: string;
    qty: number;
    averageCost: number;
    remark?: string;
  }>;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin-portfolios/${adminPortfolioId}/imports/preview`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(previewData),
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to preview reset snapshot');
  }
  return res.json();
}

// 7. Preview Reset CSV (POST - multipart/form-data)
export async function previewResetCsv(adminPortfolioId: string, formData: FormData): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin-portfolios/${adminPortfolioId}/imports/preview`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      // ไม่ต้องใส่ Content-Type เมื่อใช้ FormData เพราะ browser จะจัดการ boundary ให้เอง
      'Authorization': `Bearer ${authToken}`,
    },
    body: formData,
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to preview reset CSV');
  }
  return res.json();
}

// 8. Preview Transactions (POST)
export async function previewTransactions(adminPortfolioId: string, transactionData: {
  mode: string;
  cashDeposit: number;
  cashWithdraw: number;
  asOfDate: string;
  timezone: string;
  remark: string;
  idempotencyKey: string;
  items: Array<{
    ticker: string;
    action: string;
    qty: number;
    price: number;
    transactionAt: string;
    remark?: string;
  }>;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin-portfolios/${adminPortfolioId}/imports/preview`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(transactionData),
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to preview transactions');
  }
  return res.json();
}
export async function commitImportPreview(adminPortfolioId: string, commitData?: { previewToken?: string; confirmationCode?: string }): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin-portfolios/${adminPortfolioId}/imports/commit`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(commitData || {}),
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to commit import preview');
  }
  return res.json();
}
// 10. Get Import Job Status (GET)
export async function getImportJob(adminPortfolioId: string, importJobId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin-portfolios/${adminPortfolioId}/imports/${importJobId}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to get import job');
  }
  return res.json();
}

// ==================== SNAPSHOTS API ====================

// 11. List Snapshots (GET)
export async function listSnapshots(adminPortfolioId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin-portfolios/${adminPortfolioId}/snapshots`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to list snapshots');
  }
  return res.json();
}

// 12. Get Snapshot Detail (GET)
export async function getSnapshotDetail(adminPortfolioId: string, snapshotId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin-portfolios/${adminPortfolioId}/snapshots/${snapshotId}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to get snapshot detail');
  }
  return res.json();
}

// 13. Rollback Snapshot (POST)
export async function rollbackSnapshot(adminPortfolioId: string, snapshotId: string, rollbackData?: { remark?: string }): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin-portfolios/${adminPortfolioId}/snapshots/${snapshotId}/rollback`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(rollbackData || {}),
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to rollback snapshot');
  }
  return res.json();
}

// ==================== PUBLIC READ API ====================

// 14. List Public Portfolios (GET - ไม่ต้องใช้ Token)
export async function listPublicPortfolios(): Promise<any> {
  const url = `${API_URL}/v2/public-portfolios`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to list public portfolios');
  }
  return res.json();
}

// 15. Get Public Portfolio by Slug (GET - ไม่ต้องใช้ Token)
export async function getPublicPortfolioBySlug(slug: string): Promise<any> {
  const url = `${API_URL}/v2/public-portfolios/${slug}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) {
    await handleApiError(res, 'Failed to get public portfolio');
  }
  return res.json();
}

// 1. Preview Import (โหมด JSON)
export const previewAdminPortfolioJSON = async (adminPortfolioId: string, payload: any) => {
  const authToken = getToken();

  const response = await fetch(`${API_URL}/v2/admin-portfolios/${adminPortfolioId}/imports/preview`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const error = await response.json();
    throw error;
  }
  return response.json();
};

// 2. Commit Import (ยืนยันการบันทึก)
export const commitAdminPortfolioImportJSON = async (adminPortfolioId: string, payload: { previewToken: string, confirmationCode?: string }) => {
  const authToken = getToken();
  const response = await fetch(`${API_URL}/v2/admin-portfolios/${adminPortfolioId}/imports/commit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const error = await response.json();
    throw error;
  }
  return response.json();
};

// 3. สร้างประวัติการ Export (เก็บบันทึกลงระบบว่าเราดึงข้อมูลช่วงไหนไป)
export const createGuruExportHistory = async (payload: { guruId: string, portfolioId: string, fromDate: string, toDate: string, remark?: string }) => {
  const authToken = getToken();
  const response = await fetch(`${API_URL}/v2/admin/guru-as-exports`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const error = await response.json();
    throw error;
  }
  return response.json();
};

// 4. แปะป้าย Mark สถานะ (เพื่อบอกว่า Export นี้ถูกนำไปใส่พอร์ตแอดมินแล้ว)
export const addGuruExportMark = async (exportId: string, payload: { type: string, remark: string, metadata?: any }) => {
  const authToken = getToken();
  const response = await fetch(`${API_URL}/v2/admin/guru-as-exports/${exportId}/marks`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const error = await response.json();
    throw error;
  }
  return response.json();
};

export const getGuruExportHistory = async (params?: { guruId?: string; portfolioId?: string }) => {
  const authToken = getToken();

  // แปลง Query Params ถ้ามี
  const query = new URLSearchParams(params as any).toString();
  const url = `${API_URL}/v2/admin/guru-as-exports${query ? `?${query}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw error;
  }
  return response.json();
};

// ดึงข้อมูล Preview หลายๆ วันจาก Realtime V2 (AS/AP Guru)
export const previewGuruExport = async (payload: { guruId: string, portfolioId: string, fromDate: string, toDate: string }) => {
  const authToken = getToken();
  const url = `${API_URL}/v2/admin/guru-as-exports/preview`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const error = await response.json();
    throw error;
  }
  return response.json();
};


// 1. ดึงข้อมูลเงื่อนไขและฟิลด์ที่อนุญาตให้แก้
export const getTransactionEditConstraints = async (portfolioId: string, transactionId: string) => {
  const authToken = getToken();
  const response = await fetch(`${API_URL}/v2/portfolios/${portfolioId}/transactions/${transactionId}/edit-constraints`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`
    },
  });
  if (!response.ok) throw new Error('Failed to fetch edit constraints');
  return response.json();
};

// 2. พรีวิวก่อนบันทึก (เช็ก Impact ว่า Chain จะพังไหมโดยยังไม่เซฟจริง)
export const previewTransactionEdit = async (portfolioId: string, transactionId: string, payload: any) => {
  const authToken = getToken();
  const response = await fetch(`${API_URL}/v2/portfolios/${portfolioId}/transactions/${transactionId}/edit-preview`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error('Failed to preview transaction edit');
  return response.json();
};

// 3. บันทึกการแก้ไขจริง (PATCH) ซึ่งจะคืนค่า chainStatus และ affectedTransactions กลับมา
export const patchV2Transaction = async (portfolioId: string, transactionId: string, payload: any) => {
  const authToken = getToken();
  const response = await fetch(`${API_URL}/v2/portfolios/${portfolioId}/transactions/${transactionId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw errData;
  }
  return response.json();
};


// ============================================================================
// HELP / SUPPORT CENTER ENDPOINTS (V2)
// ============================================================================

export type Topic = { id: string; slug: string; name: string; tags: string[] };
export type Category = { id: string; slug: string; name: string; description: string; topics: Topic[] };
export type Faq = { id: string; question: string; answerHtml: string; tags: string[] };
export type TopicResponse = { topic: Topic & { descriptionHtml?: string }; faqs: Faq[] };
export type SearchItem = Faq & { category: Pick<Category, 'slug' | 'name'>; topic: Pick<Topic, 'slug' | 'name'> };

const HELP_API_BASE = `${API_URL}/v2/help`;

// Helper เฉพาะสำหรับเรียก Help API พร้อมดักจับ Error ตามสไตล์โปรเจกต์
async function fetchHelpApi<T>(path: string): Promise<T> {
  const response = await fetch(`${HELP_API_BASE}${path}`);
  if (!response.ok) {
    await handleApiError(response, 'Failed to fetch help center data');
  }
  const json = await response.json();
  if (!json.success) {
    throw new Error(json.th_message || 'เกิดข้อผิดพลาดในระบบช่วยเหลือ');
  }
  return json.data;
}

// 1. ดึงหมวดหมู่และโครงสร้าง Navigation ทั้งหมด
export async function getHelpNavigation(): Promise<Category[]> {
  return fetchHelpApi<Category[]>('/navigation');
}

// 2. ดึงรายละเอียดหัวข้อ (Topic) และรายการคำถาม (FAQ) ภายในหมวดหมู่
export async function getHelpTopicDetail(categorySlug: string, topicSlug: string): Promise<TopicResponse> {
  return fetchHelpApi<TopicResponse>(`/categories/${encodeURIComponent(categorySlug)}/topics/${encodeURIComponent(topicSlug)}`);
}

// 3. ค้นหาคำถาม (FAQ Search)
export async function searchHelpFaq(query: string): Promise<{ items: SearchItem[] }> {
  return fetchHelpApi<{ items: SearchItem[] }>(`/search?q=${encodeURIComponent(query)}`);
}




// ============================================================================
// ADMIN HELP CENTER CMS ENDPOINTS (V2)
// ============================================================================

// --- 1. Categories Management ---

// 1.1 Admin - List categories
export async function adminListCategories(): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin/help/categories`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) await handleApiError(res, 'Failed to list admin categories');
  return res.json();
}

// 1.2 Create Category
export async function adminCreateCategory(categoryData: {
  slug: string;
  name: string;
  description: string;
  order: number;
  status: string;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin/help/categories`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(categoryData),
  });

  if (!res.ok) await handleApiError(res, 'Failed to create category');
  return res.json();
}

// 1.3 Update Category
export async function adminUpdateCategory(catId: string, categoryData: {
  name: string;
  description: string;
  order: number;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin/help/categories/${catId}`;
  const res = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(categoryData),
  });

  if (!res.ok) await handleApiError(res, 'Failed to update category');
  return res.json();
}

// 1.4 Delete Category
export async function adminDeleteCategory(catId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin/help/categories/${catId}`;
  const res = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) await handleApiError(res, 'Failed to delete category');
  return res.json();
}


// --- 2. Topics Management ---

// 2.1 Create Topic
export async function adminCreateTopic(topicData: {
  categoryId: string;
  slug: string;
  name: string;
  descriptionHtml: string;
  tags: string[];
  order: number;
  status: string;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin/help/topics`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(topicData),
  });

  if (!res.ok) await handleApiError(res, 'Failed to create topic');
  return res.json();
}

// 2.2 Admin - List topics (รองรับ Query Params เช่น categoryId, status, includeDeleted)
export async function adminListTopics(options?: {
  categoryId?: string;
  status?: string;
  includeDeleted?: string;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const params = new URLSearchParams();
  if (options?.categoryId) params.append('categoryId', options.categoryId);
  if (options?.status) params.append('status', options.status);
  if (options?.includeDeleted) params.append('includeDeleted', options.includeDeleted);

  const url = `${API_URL}/v2/admin/help/topics${params.toString() ? `?${params.toString()}` : ''}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) await handleApiError(res, 'Failed to list admin topics');
  return res.json();
}

// 2.3 Update Topic
export async function adminUpdateTopic(topicId: string, topicData: {
  name: string;
  descriptionHtml: string;
  tags: string[];
  order: number;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin/help/topics/${topicId}`;
  const res = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(topicData),
  });

  if (!res.ok) await handleApiError(res, 'Failed to update topic');
  return res.json();
}

// 2.4 Delete Topic
export async function adminDeleteTopic(topicId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin/help/topics/${topicId}`;
  const res = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) await handleApiError(res, 'Failed to delete topic');
  return res.json();
}


// ============================================================================
// ADMIN HELP CENTER CMS: FAQS & TAGS ENDPOINTS (V2)
// ============================================================================

// --- 3. FAQs Management ---

// 3.1 Create FAQ
export async function adminCreateFaq(faqData: {
  topicId: string;
  question: string;
  answerHtml: string;
  tags: string[];
  keywords: string[];
  order: number;
  status: string;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin/help/faqs`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(faqData),
  });

  if (!res.ok) await handleApiError(res, 'Failed to create FAQ');
  return res.json();
}

// 3.2 Publishing (Update FAQ Status/Publishing)
export async function adminPublishFaq(faqId: string, statusData: { status: string }): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin/help/faqs/${faqId}`;
  const res = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(statusData),
  });

  if (!res.ok) await handleApiError(res, 'Failed to update FAQ status');
  return res.json();
}

// 3.3 Admin - List FAQs (รองรับ Query Params เช่น topicId, status, includeDeleted)
export async function adminListFaqs(options?: {
  topicId?: string;
  status?: string;
  includeDeleted?: string;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const params = new URLSearchParams();
  if (options?.topicId) params.append('topicId', options.topicId);
  if (options?.status) params.append('status', options.status);
  if (options?.includeDeleted) params.append('includeDeleted', options.includeDeleted);

  const url = `${API_URL}/v2/admin/help/faqs${params.toString() ? `?${params.toString()}` : ''}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) await handleApiError(res, 'Failed to list admin FAQs');
  return res.json();
}

// 3.4 Update FAQ
export async function adminUpdateFaq(faqId: string, faqData: {
  question: string;
  answerHtml: string;
  tags: string[];
  keywords: string[];
  order: number;
}): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin/help/faqs/${faqId}`;
  const res = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(faqData),
  });

  if (!res.ok) await handleApiError(res, 'Failed to update FAQ');
  return res.json();
}

// 3.5 Delete FAQ
export async function adminDeleteFaq(faqId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin/help/faqs/${faqId}`;
  const res = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) await handleApiError(res, 'Failed to delete FAQ');
  return res.json();
}


// --- 4. Tags Management ---

// 4.1 Admin - List Existing Tags
export async function adminListExistingTags(): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/admin/help/tags`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) await handleApiError(res, 'Failed to list existing tags');
  return res.json();
}



// 1. ดึงรายการปันผลที่สามารถลงทุนซ้ำได้
export async function getReinvestmentCandidates(portfolioId: string): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portfolioId}/dividends/reinvestment-candidates`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${authToken}` },
  });

  if (!res.ok) await handleApiError(res, 'Failed to fetch reinvestment candidates');
  return res.json();
}

// 2. จบสถานะปันผลโดยไม่ลงทุนซ้ำ (เก็บเป็นเงินสด)
export async function completeDividendWithoutReinvest(
  portfolioId: string, 
  transactionId: string, 
  reason: string
): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portfolioId}/dividends/${transactionId}/complete`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json', 
      'Authorization': `Bearer ${authToken}` 
    },
    body: JSON.stringify({ reason }),
  });

  if (!res.ok) await handleApiError(res, 'Failed to complete dividend');
  return res.json();
}

// 3. สร้างรายการซื้อ (BUY) สำหรับลงทุนซ้ำ
export async function createDividendReinvestment(
  portfolioId: string, 
  transactionData: {
    type: string;
    subPortId: string;
    ticker: string;
    quantity: number;
    price: number;
    fee: number;
    source: string;
    sourceTransactionId: string;
  }
): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/v2/portfolios/${portfolioId}/transactions`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json', 
      'Authorization': `Bearer ${authToken}` 
    },
    body: JSON.stringify(transactionData),
  });

  if (!res.ok) await handleApiError(res, 'Failed to create reinvestment transaction');
  return res.json();
}

// ============================================================================
// Price Alerts
// ============================================================================
// ดู price-alert-api-spec.md — ไม่มี /api prefix, ทุก endpoint (ยกเว้น admin)
// ใช้ token ของ user เอง ห้ามส่ง userId/currentPrice จากฝั่ง client

export type PriceAlertOperator = 'GTE' | 'LTE';
export type PriceAlertStatus = 'PENDING_INDEX' | 'ACTIVE' | 'TRIGGERED' | 'CANCELLED' | 'EXPIRED';

export interface PriceAlert {
  id: string;
  userId: string;
  symbol: string;
  targetPrice: string;
  operator: PriceAlertOperator;
  status: PriceAlertStatus;
  armedPrice: string;
  armedAt: string;
  createdAt: string;
  updatedAt: string;
  expiresAt?: string;
  providerAfter?: number;
  indexedAt?: string;
  replaces?: string;
  replacedBy?: string;
  triggeredPrice?: string;
  triggeredAt?: string;
  triggerWindowHigh?: string;
  triggerWindowLow?: string;
  triggerProviderTimestamp?: string;
  triggerEventId?: string;
  notificationDedupeKey?: string;
  [key: string]: any;
}

export interface PriceAlertInput {
  symbol: string;
  targetPrice: string;
  operator: PriceAlertOperator;
  expiresAt?: string;
}

export interface PriceAlertUpdateInput {
  targetPrice?: string;
  operator?: PriceAlertOperator;
  expiresAt?: string;
}

export interface PriceAlertPreviewItemResult {
  index: number;
  symbol: string | null;
  targetPrice: string | null;
  operator: PriceAlertOperator | null;
  valid: boolean;
  error?: { code: string; message: string; th_message: string };
}

export interface PriceAlertPreviewResult {
  valid: boolean;
  currentPrices: Record<string, string>;
  items: PriceAlertPreviewItemResult[];
  quota: { active: number; limit: number; remaining: number };
}

export interface PriceAlertListResult {
  items: PriceAlert[];
  nextCursor: string | null;
}

export interface PriceAlertSummary {
  counts: { pending: number; active: number; triggered: number; cancelled: number; expired: number };
  live: number;
  symbols: number;
  limit: number;
  remaining: number;
}

export interface PriceAlertConfig {
  enabled: boolean;
  accepting: boolean;
  maxActive: number;
  bulkMax: number;
  priceScale: number;
  retentionDays: number;
  supportedOperators: PriceAlertOperator[];
}

export interface PriceAlertBulkCancelResult {
  items: Array<{ id: string; status: PriceAlertStatus }>;
}

export interface PriceAlertAdminHealth {
  enabled: boolean;
  nodeRole: string;
  workerRequired: boolean;
  consumerGroup: string;
  consumerName: string;
  accepting: boolean;
  redis: 'connected' | 'unavailable';
  contract: 'unknown' | 'verified' | 'mismatch' | 'unavailable';
  armedConsumer: string;
  triggerConsumer: string;
  reconciliation: string;
  lastArmedMessageAt: string | null;
  lastTriggerMessageAt: string | null;
  lastTriggerProcessedAt: string | null;
  lastTriggerErrorAt: string | null;
  lastError: string | null;
}

export interface PriceAlertAdminRebuildResult {
  status: string;
  mode: string;
  workerRequired: boolean;
}

export interface PriceAlertAdminStats {
  asOf: string;
  cacheSeconds: number;
  alerts: { pending: number; active: number; triggered24h: number; expired24h: number; cancelled24h: number };
  symbols: { active: number };
  stream: { available: boolean; pending: number | null; lagSeconds: number | null };
  reconciliation: { missingDetectedTotal: number; scope: string };
}

// Create a price alert
export async function createPriceAlert(payload: PriceAlertInput): Promise<PriceAlert> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/price-alerts`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) await handleApiError(res, 'Failed to create price alert');
  return res.json();
}

// Bulk create price alerts
export async function bulkCreatePriceAlerts(alerts: PriceAlertInput[]): Promise<PriceAlert[]> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/price-alerts/bulk`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify({ alerts }),
  });

  if (!res.ok) await handleApiError(res, 'Failed to bulk create price alerts');
  return res.json();
}

// List price alerts
export async function getPriceAlerts(options?: {
  // ส่งหลายสถานะได้ (backend รับแบบคั่นด้วย comma ในพารามิเตอร์เดียว ห้ามส่ง status ซ้ำหลายตัว)
  status?: PriceAlertStatus | PriceAlertStatus[];
  symbol?: string;
  limit?: number;
  after?: string;
}): Promise<PriceAlertListResult> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const params = new URLSearchParams();
  if (options?.status) {
    params.append('status', Array.isArray(options.status) ? options.status.join(',') : options.status);
  }
  if (options?.symbol) params.append('symbol', options.symbol);
  if (options?.limit !== undefined) params.append('limit', String(options.limit));
  if (options?.after) params.append('after', options.after);

  const url = `${API_URL}/price-alerts${params.toString() ? `?${params.toString()}` : ''}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) await handleApiError(res, 'Failed to fetch price alerts');
  return res.json();
}

// Preview price alerts (advisory validation, no writes)
export async function previewPriceAlerts(alerts: PriceAlertInput[]): Promise<PriceAlertPreviewResult> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/price-alerts/preview`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify({ alerts }),
  });

  if (!res.ok) await handleApiError(res, 'Failed to preview price alerts');
  return res.json();
}

// Bulk cancel price alerts
export async function bulkCancelPriceAlerts(ids: string[]): Promise<PriceAlertBulkCancelResult> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/price-alerts/bulk/cancel`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify({ ids }),
  });

  if (!res.ok) await handleApiError(res, 'Failed to bulk cancel price alerts');
  return res.json();
}

// Get price alert summary (counts + quota for current user)
export async function getPriceAlertSummary(): Promise<PriceAlertSummary> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/price-alerts/summary`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) await handleApiError(res, 'Failed to fetch price alert summary');
  return res.json();
}

// Get safe frontend configuration
export async function getPriceAlertConfig(): Promise<PriceAlertConfig> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/price-alerts/config`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) await handleApiError(res, 'Failed to fetch price alert config');
  return res.json();
}

// Get a single price alert by id
export async function getPriceAlertById(id: string): Promise<PriceAlert> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/price-alerts/${id}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) await handleApiError(res, 'Failed to fetch price alert');
  return res.json();
}

// Update a price alert. Returns the REPLACEMENT alert (new id); see `replaces`.
export async function updatePriceAlert(id: string, payload: PriceAlertUpdateInput): Promise<PriceAlert> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/price-alerts/${id}`;
  const res = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) await handleApiError(res, 'Failed to update price alert');
  return res.json();
}

// Cancel a price alert. Returns the final Alert (not empty).
export async function cancelPriceAlert(id: string): Promise<PriceAlert> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/price-alerts/${id}`;
  const res = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) await handleApiError(res, 'Failed to cancel price alert');
  return res.json();
}

// Admin: local runtime health snapshot
export async function getAdminPriceAlertHealth(): Promise<PriceAlertAdminHealth> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/admin/price-alerts/health`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) await handleApiError(res, 'Failed to fetch price alert admin health');
  return res.json();
}

// Admin: queue non-destructive Mongo repair
export async function adminRebuildPriceAlerts(): Promise<PriceAlertAdminRebuildResult> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/admin/price-alerts/rebuild`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) await handleApiError(res, 'Failed to queue price alert rebuild');
  return res.json();
}

// Admin: cached business/system aggregates
export async function getAdminPriceAlertStats(): Promise<PriceAlertAdminStats> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/admin/price-alerts/stats`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!res.ok) await handleApiError(res, 'Failed to fetch price alert admin stats');
  return res.json();
}

// ============================================================================
// Portfolio Tools
// ============================================================================

export type ReduceAllocationPrice =
  // คริปโต: ส่งราคาตรงตัวใน decimalValue (มีลำดับเหนือ value) ตาม spec
  | { mode: 'MANUAL'; value: number; decimalValue?: string }
  | { mode: 'MARKET' };

export interface ReduceAllocationCalculateInput {
  portfolioVersion: 'V1' | 'V2';
  portfolioId: string;
  subPortId: string;
  symbol: string;
  targetRatio: number;
  price: ReduceAllocationPrice;
  // includeCostBasisProjection: true ให้ backend คำนวณ/ส่งต้นทุนเฉลี่ยหลังขายกลับมาด้วย (ไม่งั้น response จะไม่มี field นี้เลย)
  options?: { includeCostBasisProjection?: boolean };
}

// คำนวณจำนวนหุ้นที่ต้องขายเพื่อลดสัดส่วน (allocation) ของ symbol ให้เหลือ targetRatio
// price.mode 'MANUAL' ให้ใส่ราคาที่ user กำหนดเอง, 'MARKET' ให้ backend ใช้ราคาตลาดปัจจุบัน
export async function calculateReduceAllocation(payload: ReduceAllocationCalculateInput): Promise<any> {
  const authToken = getToken();
  if (!authToken) throw new Error('No token found');

  const url = `${API_URL}/tools/portfolio/reduce-allocation/calculate`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) await handleApiError(res, 'Failed to calculate reduce allocation');
  const json = await res.json();
  // Response มาห่อด้วย { success, data } เสมอ ต้องแกะ data ออกมาก่อนส่งต่อให้ UI
  return json?.data ?? json;
}